/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, default-case */
import React, { useState, useEffect, useRef } from 'react';
import {
  FaLightbulb,
  FaCrosshairs,
  FaUsers,
  FaChartLine,
  FaBullhorn,
  FaPlus,
  FaTimes,
  FaExpand,
  FaPaperPlane,
  FaHistory,
  FaTrash,
  FaPaperclip,
  FaEnvelope,
  FaDatabase,
  FaClock,
  FaCode,
  FaSlack,
  FaGoogle,
  FaPlay,
  FaStop,
  FaFilter,
  FaCodeBranch,
  FaCloudUploadAlt,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaTwitter,
  FaLinkedin,
  FaImage,
  FaVideo,
  FaEdit,
  FaCog,
  FaSave,
  FaCopy,
  FaRobot,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLink,
  FaShoppingCart,
  FaCreditCard,
  FaCashRegister,
  FaFolder,
  FaFolderOpen,
  FaFile,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaDownload
} from 'react-icons/fa';
import togetherAIService from '../services/togetherAIService';
import workflowAPIService from '../services/workflowAPIService';
import FlowCanvas from './FlowCanvas';
import NodeSettingsPanel from './NodeSettingsPanel';
import { useAuth } from '../context/AuthContext';
import './StrategyCanvas.css';

function StrategyCanvas({ initialPrompt, onClose, inline = false }) {
  const { authenticatedProviders, currentBusiness } = useAuth();
  const [canvasNodes, setCanvasNodes] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [connections, setConnections] = useState([]);
  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);
  const [showWorkflowHistory, setShowWorkflowHistory] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [nodeSettings, setNodeSettings] = useState({});
  const [isDraggingNode, setIsDraggingNode] = useState(false);
  const [draggedNodeId, setDraggedNodeId] = useState(null);
  const [serviceConnections, setServiceConnections] = useState({});
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [showSaveWorkflow, setShowSaveWorkflow] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowCategory, setWorkflowCategory] = useState('general');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState('');
  const [showNewWorkflowConfirm, setShowNewWorkflowConfirm] = useState(false);
  const [highlightedStep, setHighlightedStep] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [customCategories, setCustomCategories] = useState(['General', 'Marketing', 'Sales', 'E-commerce', 'Analytics', 'Customer Service']);
  const [workflowOutput, setWorkflowOutput] = useState(null);
  const [showOutputViewer, setShowOutputViewer] = useState(false);
  const [draggingConnection, setDraggingConnection] = useState(null);
  const [connectionPath, setConnectionPath] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connectionPreview, setConnectionPreview] = useState(null);
  const canvasRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Basic workflow execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentExecutingStep, setCurrentExecutingStep] = useState(null);
  const [executedSteps, setExecutedSteps] = useState(new Set());
  
  // Connection authorization state
  const [showConnectionAuth, setShowConnectionAuth] = useState(false);
  const [currentAuthPlatform, setCurrentAuthPlatform] = useState(null);
  const [connectedServices, setConnectedServices] = useState(new Set());
  const [authLoading, setAuthLoading] = useState(false);

  // Platform-specific configuration for OAuth and API connections
  const platformConfigs = {
    google: {
      name: 'Google',
      color: '#4285F4',
      icon: FaGoogle,
      authType: 'oauth2',
      scopes: ['https://www.googleapis.com/auth/userinfo.email', 'https://www.googleapis.com/auth/drive', 'https://www.googleapis.com/auth/spreadsheets', 'https://www.googleapis.com/auth/calendar'],
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id',
      description: 'Connect to access Google Drive, Sheets, Calendar, and Analytics'
    },
    gmail: {
      name: 'Gmail',
      color: '#EA4335',
      icon: FaEnvelope,
      authType: 'oauth2',
      scopes: ['https://www.googleapis.com/auth/gmail.send', 'https://www.googleapis.com/auth/gmail.readonly'],
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id',
      description: 'Connect to send and read emails through Gmail'
    },
    facebook: {
      name: 'Facebook',
      color: '#1877F2',
      icon: FaFacebook,
      authType: 'oauth2',
      scopes: ['pages_manage_posts', 'pages_read_engagement', 'publish_to_groups'],
      appId: process.env.REACT_APP_FACEBOOK_APP_ID || 'your-facebook-app-id',
      description: 'Connect to manage Facebook pages and posts'
    },
    instagram: {
      name: 'Instagram',
      color: '#E4405F',
      icon: FaInstagram,
      authType: 'oauth2',
      scopes: ['user_profile', 'user_media'],
      appId: process.env.REACT_APP_FACEBOOK_APP_ID || 'your-facebook-app-id',
      description: 'Connect to manage Instagram posts and media'
    },
    twitter: {
      name: 'Twitter/X',
      color: '#1DA1F2',
      icon: FaTwitter,
      authType: 'oauth2',
      scopes: ['tweet.read', 'tweet.write', 'users.read'],
      clientId: process.env.REACT_APP_TWITTER_CLIENT_ID || 'your-twitter-client-id',
      description: 'Connect to create and manage tweets'
    },
    linkedin: {
      name: 'LinkedIn',
      color: '#0A66C2',
      icon: FaLinkedin,
      authType: 'oauth2',
      scopes: ['r_liteprofile', 'w_member_social'],
      clientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID || 'your-linkedin-client-id',
      description: 'Connect to create and manage LinkedIn posts'
    },
    youtube: {
      name: 'YouTube',
      color: '#FF0000',
      icon: FaYoutube,
      authType: 'oauth2',
      scopes: ['https://www.googleapis.com/auth/youtube.upload'],
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id',
      description: 'Connect to upload and manage YouTube videos'
    },
    slack: {
      name: 'Slack',
      color: '#4A154B',
      icon: FaSlack,
      authType: 'oauth2',
      scopes: ['chat:write', 'channels:read'],
      clientId: process.env.REACT_APP_SLACK_CLIENT_ID || 'your-slack-client-id',
      description: 'Connect to send messages and manage Slack channels'
    },
    shopify: {
      name: 'Shopify',
      color: '#96BF47',
      icon: FaShoppingCart,
      authType: 'oauth2',
      scopes: ['read_orders', 'write_orders', 'read_products', 'write_products'],
      clientId: process.env.REACT_APP_SHOPIFY_CLIENT_ID || 'your-shopify-client-id',
      description: 'Connect to manage your Shopify store and orders'
    },
    square: {
      name: 'Square',
      color: '#3E4348',
      icon: FaCreditCard,
      authType: 'oauth2',
      scopes: ['PAYMENTS_READ', 'PAYMENTS_WRITE'],
      clientId: process.env.REACT_APP_SQUARE_CLIENT_ID || 'your-square-client-id',
      description: 'Connect to process payments and manage Square transactions'
    },
    stripe: {
      name: 'Stripe',
      color: '#635BFF',
      icon: FaCreditCard,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_STRIPE_API_KEY || 'sk_test_your-stripe-key',
      description: 'Connect using your Stripe API key to process payments'
    },
    openai: {
      name: 'OpenAI',
      color: '#00A67E',
      icon: FaRobot,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_OPENAI_API_KEY || 'sk-your-openai-key',
      description: 'Connect using your OpenAI API key for AI features'
    },
    airtable: {
      name: 'Airtable',
      color: '#18BFFF',
      icon: FaDatabase,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_AIRTABLE_API_KEY || 'your-airtable-key',
      description: 'Connect using your Airtable API key to manage databases'
    },
    notion: {
      name: 'Notion',
      color: '#000000',
      icon: FaEdit,
      authType: 'oauth2',
      scopes: ['read', 'write'],
      clientId: process.env.REACT_APP_NOTION_CLIENT_ID || 'your-notion-client-id',
      description: 'Connect to create and manage Notion pages and databases'
    },
    discord: {
      name: 'Discord',
      color: '#5865F2',
      icon: FaRobot,
      authType: 'oauth2',
      scopes: ['bot', 'applications.commands'],
      clientId: process.env.REACT_APP_DISCORD_CLIENT_ID || 'your-discord-client-id',
      description: 'Connect to send messages and manage Discord servers'
    },
    telegram: {
      name: 'Telegram',
      color: '#0088CC',
      icon: FaEnvelope,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_TELEGRAM_BOT_TOKEN || 'your-bot-token',
      description: 'Connect using your Telegram bot token'
    },
    whatsapp: {
      name: 'WhatsApp Business',
      color: '#25D366',
      icon: FaEnvelope,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_WHATSAPP_TOKEN || 'your-whatsapp-token',
      description: 'Connect using WhatsApp Business API credentials'
    },
    mixpanel: {
      name: 'Mixpanel',
      color: '#7856FF',
      icon: FaChartLine,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_MIXPANEL_TOKEN || 'your-mixpanel-token',
      description: 'Connect using your Mixpanel project token'
    },
    segment: {
      name: 'Segment',
      color: '#52BD94',
      icon: FaChartLine,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_SEGMENT_WRITE_KEY || 'your-segment-write-key',
      description: 'Connect using your Segment write key'
    },
    paypal: {
      name: 'PayPal',
      color: '#0070BA',
      icon: FaCreditCard,
      authType: 'oauth2',
      scopes: ['https://uri.paypal.com/services/payments'],
      clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || 'your-paypal-client-id',
      description: 'Connect to process PayPal payments'
    },
    toast: {
      name: 'Toast POS',
      color: '#FF6B35',
      icon: FaCashRegister,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_TOAST_API_KEY || 'your-toast-api-key',
      description: 'Connect using your Toast POS API credentials'
    },
    woocommerce: {
      name: 'WooCommerce',
      color: '#96588A',
      icon: FaShoppingCart,
      authType: 'api_key',
      apiKey: process.env.REACT_APP_WOOCOMMERCE_KEY || 'ck_your-woocommerce-key',
      description: 'Connect using your WooCommerce REST API credentials'
    }
  };

  // Connection authorization functions
  const openConnectionAuth = (platform) => {
    setCurrentAuthPlatform(platform);
    setShowConnectionAuth(true);
  };

  const closeConnectionAuth = () => {
    setShowConnectionAuth(false);
    setCurrentAuthPlatform(null);
    setAuthLoading(false);
  };

  const handleOAuthLogin = async (platform) => {
    setAuthLoading(true);
    const config = platformConfigs[platform];
    
    try {
      if (config.authType === 'oauth2') {
        // OAuth2 flow
        const redirectUri = `${window.location.origin}/auth/callback`;
        let authUrl;

        switch (platform) {
          case 'google':
          case 'gmail':
          case 'youtube':
            authUrl = `https://accounts.google.com/oauth2/v2/auth?client_id=${config.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(config.scopes.join(' '))}&response_type=code&access_type=offline`;
            break;
          case 'facebook':
          case 'instagram':
            authUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${config.appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(config.scopes.join(','))}&response_type=code`;
            break;
          case 'twitter':
            authUrl = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${config.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(config.scopes.join(' '))}&state=state&code_challenge=challenge&code_challenge_method=plain`;
            break;
          case 'linkedin':
            authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${config.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(config.scopes.join(' '))}`;
            break;
          case 'slack':
            authUrl = `https://slack.com/oauth/v2/authorize?client_id=${config.clientId}&scope=${encodeURIComponent(config.scopes.join(','))}&redirect_uri=${encodeURIComponent(redirectUri)}`;
            break;
          case 'shopify':
            // Shopify requires store-specific URL
            const shopifyStore = prompt('Enter your Shopify store name (e.g., "mystore" for mystore.myshopify.com):');
            if (!shopifyStore) {
              setAuthLoading(false);
              return;
            }
            authUrl = `https://${shopifyStore}.myshopify.com/admin/oauth/authorize?client_id=${config.clientId}&scope=${encodeURIComponent(config.scopes.join(','))}&redirect_uri=${encodeURIComponent(redirectUri)}`;
            break;
          case 'square':
            authUrl = `https://connect.squareup.com/oauth2/authorize?client_id=${config.clientId}&response_type=code&scope=${encodeURIComponent(config.scopes.join(' '))}`;
            break;
          case 'notion':
            authUrl = `https://api.notion.com/v1/oauth/authorize?client_id=${config.clientId}&response_type=code&owner=user&redirect_uri=${encodeURIComponent(redirectUri)}`;
            break;
          case 'discord':
            authUrl = `https://discord.com/api/oauth2/authorize?client_id=${config.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(config.scopes.join('%20'))}`;
            break;
          case 'paypal':
            authUrl = `https://www.sandbox.paypal.com/connect?flowEntry=static&client_id=${config.clientId}&scope=${encodeURIComponent(config.scopes.join(' '))}&redirect_uri=${encodeURIComponent(redirectUri)}`;
            break;
          default:
            throw new Error(`OAuth not configured for ${platform}`);
        }

        // Open OAuth popup
        const popup = window.open(authUrl, 'oauth', 'width=500,height=600,scrollbars=yes,resizable=yes');
        
        // Listen for OAuth callback
        const pollTimer = window.setInterval(() => {
          try {
            if (popup.closed) {
              window.clearInterval(pollTimer);
              // Simulate successful connection for demo
              setConnectedServices(prev => new Set([...prev, platform]));
              setAuthLoading(false);
              closeConnectionAuth();
            }
          } catch (e) {
            // Handle popup blocked or other errors
          }
        }, 1000);

        // Timeout after 5 minutes
        setTimeout(() => {
          window.clearInterval(pollTimer);
          if (!popup.closed) {
            popup.close();
          }
          setAuthLoading(false);
        }, 300000);

      } else if (config.authType === 'api_key') {
        // API key flow - will be handled by form submission
        setAuthLoading(false);
      }
    } catch (error) {
      console.error('OAuth error:', error);
      alert(`Failed to connect to ${config.name}. Please check your configuration.`);
      setAuthLoading(false);
    }
  };

  const handleApiKeySubmit = async (platform, apiKey, additionalData = {}) => {
    setAuthLoading(true);
    const config = platformConfigs[platform];
    
    try {
      // Validate API key format
      if (!apiKey || apiKey.trim() === '') {
        throw new Error('API key is required');
      }

      // Platform-specific validation
      switch (platform) {
        case 'stripe':
          if (!apiKey.startsWith('sk_')) {
            throw new Error('Invalid Stripe API key format. Should start with sk_');
          }
          break;
        case 'openai':
          if (!apiKey.startsWith('sk-')) {
            throw new Error('Invalid OpenAI API key format. Should start with sk-');
          }
          break;
        case 'woocommerce':
          if (!apiKey.startsWith('ck_')) {
            throw new Error('Invalid WooCommerce consumer key format. Should start with ck_');
          }
          break;
      }

      // Simulate API validation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Store connection (in real app, this would be encrypted and stored securely)
      const connectionData = {
        platform,
        apiKey,
        ...additionalData,
        connectedAt: new Date().toISOString()
      };
      
      // Save to localStorage for demo (use secure backend in production)
      const existingConnections = JSON.parse(localStorage.getItem('platformConnections') || '{}');
      existingConnections[platform] = connectionData;
      localStorage.setItem('platformConnections', JSON.stringify(existingConnections));
      
      setConnectedServices(prev => new Set([...prev, platform]));
      setAuthLoading(false);
      closeConnectionAuth();
      
      alert(`Successfully connected to ${config.name}!`);
    } catch (error) {
      console.error('API key validation error:', error);
      alert(error.message);
      setAuthLoading(false);
    }
  };

  // Check for existing connections on component mount
  useEffect(() => {
    const existingConnections = JSON.parse(localStorage.getItem('platformConnections') || '{}');
    const connectedPlatforms = Object.keys(existingConnections);
    setConnectedServices(new Set(connectedPlatforms));
  }, []);

  // Workflow node types - Activepieces-inspired integrations
  const workflowNodeTypes = [
    // === TRIGGERS ===
    // Email & Communication Triggers
    { 
      id: 'trigger-email-gmail', 
      name: 'Gmail Trigger', 
      subtitle: 'New Email Received',
      description: 'Triggers when a new email is received in Gmail',
      icon: FaEnvelope, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['gmail'],
      connectionSettings: {
        gmail: { required: true, scopes: ['gmail.readonly'] }
      }
    },
    { 
      id: 'trigger-slack-message', 
      name: 'Slack Trigger', 
      subtitle: 'New Message Received',
      description: 'Triggers when a new message is received in Slack',
      icon: FaSlack, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['slack']
    },
    
    // Social Media Triggers
    { 
      id: 'trigger-instagram-post', 
      name: 'Instagram - New Post', 
      icon: FaInstagram, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['instagram'],
      connectionSettings: {
        instagram: { required: true, permissions: ['instagram_basic'] }
      }
    },
    { 
      id: 'trigger-facebook-page', 
      name: 'Facebook - Page Update', 
      icon: FaFacebook, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['facebook'],
      connectionSettings: {
        facebook: { required: true, permissions: ['pages_read_engagement'] }
      }
    },
    { 
      id: 'trigger-twitter-mention', 
      name: 'Twitter - New Mention', 
      icon: FaTwitter, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['twitter']
    },
    { 
      id: 'trigger-youtube-upload', 
      name: 'YouTube - New Upload', 
      icon: FaYoutube, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['youtube']
    },
    
    // Data & Form Triggers
    { id: 'trigger-webhook', name: 'Webhook - HTTP Request', icon: FaCode, category: 'trigger', color: '#10B981' },
    { id: 'trigger-form-typeform', name: 'Typeform - Form Response', icon: FaUsers, category: 'trigger', color: '#10B981' },
    { id: 'trigger-schedule', name: 'Schedule - Timer', icon: FaClock, category: 'trigger', color: '#10B981' },
    { 
      id: 'trigger-google-sheets', 
      name: 'Google Sheets - New Row', 
      icon: FaGoogle, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['google']
    },
    
    // E-commerce Triggers
    { 
      id: 'trigger-shopify-order', 
      name: 'Shopify - New Order', 
      icon: FaShoppingCart, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['shopify']
    },
    { 
      id: 'trigger-square-payment', 
      name: 'Square - New Payment', 
      icon: FaCreditCard, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['square']
    },
    
    // Social Media Actions
    { 
      id: 'action-instagram-post', 
      name: 'Post to Instagram', 
      icon: FaInstagram, 
      category: 'social', 
      color: '#E4405F',
      requiredConnections: ['instagram'],
      connectionSettings: {
        instagram: { required: true, permissions: ['pages_show_list', 'pages_read_engagement', 'instagram_basic'] }
      }
    },
    { 
      id: 'action-facebook-post', 
      name: 'Post to Facebook', 
      icon: FaFacebook, 
      category: 'social', 
      color: '#1877F2',
      requiredConnections: ['facebook'],
      connectionSettings: {
        facebook: { required: true, permissions: ['pages_manage_posts', 'publish_to_groups'] }
      }
    },
    { 
      id: 'action-twitter-post', 
      name: 'Post to Twitter', 
      icon: FaTwitter, 
      category: 'social', 
      color: '#1DA1F2',
      requiredConnections: ['twitter'],
      connectionSettings: {
        twitter: { required: true, permissions: ['tweet.write', 'users.read'] }
      }
    },
    { 
      id: 'action-linkedin-post', 
      name: 'Post to LinkedIn', 
      icon: FaLinkedin, 
      category: 'social', 
      color: '#0A66C2',
      requiredConnections: ['linkedin'],
      connectionSettings: {
        linkedin: { required: true, permissions: ['w_member_social'] }
      }
    },
    { 
      id: 'action-youtube-upload', 
      name: 'Upload to YouTube', 
      icon: FaYoutube, 
      category: 'social', 
      color: '#FF0000',
      requiredConnections: ['youtube'],
      connectionSettings: {
        youtube: { required: true, scopes: ['youtube.upload'] }
      }
    },
    
    // Regular Actions
    { 
      id: 'action-email', 
      name: 'Send Email', 
      icon: FaEnvelope, 
      category: 'action', 
      color: '#3B82F6',
      requiredConnections: ['gmail'],
      connectionSettings: {
        gmail: { required: true, scopes: ['gmail.send'] }
      }
    },
    { 
      id: 'action-slack', 
      name: 'Send Slack Message', 
      icon: FaSlack, 
      category: 'action', 
      color: '#3B82F6',
      requiredConnections: ['slack'],
      connectionSettings: {
        slack: { required: true, scopes: ['chat:write'] }
      }
    },
    { id: 'action-database', name: 'Save to Database', icon: FaDatabase, category: 'action', color: '#3B82F6' },
    { 
      id: 'action-google-sheets', 
      name: 'Add to Google Sheets', 
      icon: FaGoogle, 
      category: 'action', 
      color: '#3B82F6',
      requiredConnections: ['google'],
      connectionSettings: {
        google: { required: true, scopes: ['spreadsheets'] }
      }
    },
    { 
      id: 'action-google-analytics', 
      name: 'Pull Google Analytics', 
      icon: FaGoogle, 
      category: 'action', 
      color: '#3B82F6',
      requiredConnections: ['google'],
      connectionSettings: {
        google: { required: true, scopes: ['analytics.readonly'] }
      }
    },
    
    // E-commerce Integrations
    { 
      id: 'action-shopify-order', 
      name: 'Create Shopify Order', 
      icon: FaShoppingCart, 
      category: 'ecommerce', 
      color: '#96BF47',
      requiredConnections: ['shopify'],
      connectionSettings: {
        shopify: { required: true, scopes: ['write_orders', 'read_products'] }
      }
    },
    { 
      id: 'trigger-shopify-order', 
      name: 'Shopify Order Created', 
      icon: FaShoppingCart, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['shopify'],
      connectionSettings: {
        shopify: { required: true, scopes: ['read_orders'] }
      }
    },
    { 
      id: 'action-shopify-inventory', 
      name: 'Update Shopify Inventory', 
      icon: FaDatabase, 
      category: 'ecommerce', 
      color: '#96BF47',
      requiredConnections: ['shopify'],
      connectionSettings: {
        shopify: { required: true, scopes: ['write_inventory'] }
      }
    },
    { 
      id: 'action-square-payment', 
      name: 'Process Square Payment', 
      icon: FaCreditCard, 
      category: 'ecommerce', 
      color: '#3E4348',
      requiredConnections: ['square'],
      connectionSettings: {
        square: { required: true, permissions: ['PAYMENTS_WRITE'] }
      }
    },
    { 
      id: 'trigger-square-payment', 
      name: 'Square Payment Received', 
      icon: FaCreditCard, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['square'],
      connectionSettings: {
        square: { required: true, permissions: ['PAYMENTS_READ'] }
      }
    },
    { 
      id: 'action-toast-order', 
      name: 'Create Toast POS Order', 
      icon: FaCashRegister, 
      category: 'ecommerce', 
      color: '#FF6B35',
      requiredConnections: ['toast'],
      connectionSettings: {
        toast: { required: true, scopes: ['orders:write'] }
      }
    },
    { 
      id: 'trigger-toast-order', 
      name: 'Toast Order Created', 
      icon: FaCashRegister, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['toast'],
      connectionSettings: {
        toast: { required: true, scopes: ['orders:read'] }
      }
    },
    { 
      id: 'action-toast-menu', 
      name: 'Update Toast Menu', 
      icon: FaEdit, 
      category: 'ecommerce', 
      color: '#FF6B35',
      requiredConnections: ['toast'],
      connectionSettings: {
        toast: { required: true, scopes: ['menus:write'] }
      }
    },
    
    // AI & OpenAI Integrations
    { 
      id: 'openai-chat-completion', 
      name: 'OpenAI - Chat Completion', 
      icon: FaRobot, 
      category: 'ai', 
      color: '#00A67E',
      requiredConnections: ['openai']
    },
    { 
      id: 'openai-image-generation', 
      name: 'OpenAI - DALL-E Image', 
      icon: FaImage, 
      category: 'ai', 
      color: '#00A67E',
      requiredConnections: ['openai']
    },
    { 
      id: 'openai-transcription', 
      name: 'OpenAI - Whisper Audio', 
      icon: FaVideo, 
      category: 'ai', 
      color: '#00A67E',
      requiredConnections: ['openai']
    },
    { id: 'ai-image-generate', name: 'Generate Image (AI)', icon: FaImage, category: 'ai', color: '#9333EA' },
    { id: 'ai-video-generate', name: 'Generate Video (AI)', icon: FaVideo, category: 'ai', color: '#9333EA' },
    { id: 'ai-text-generate', name: 'Generate Text (AI)', icon: FaRobot, category: 'ai', color: '#9333EA' },
    { id: 'ai-image-edit', name: 'Edit Image (AI)', icon: FaEdit, category: 'ai', color: '#9333EA' },
    
    // Additional Data Integrations
    { 
      id: 'airtable-create-record', 
      name: 'Airtable - Create Record', 
      icon: FaDatabase, 
      category: 'data', 
      color: '#18BFFF',
      requiredConnections: ['airtable']
    },
    { 
      id: 'airtable-get-records', 
      name: 'Airtable - Get Records', 
      icon: FaDatabase, 
      category: 'data', 
      color: '#18BFFF',
      requiredConnections: ['airtable']
    },
    { 
      id: 'notion-create-page', 
      name: 'Notion - Create Page', 
      icon: FaEdit, 
      category: 'data', 
      color: '#000000',
      requiredConnections: ['notion']
    },
    { 
      id: 'notion-update-database', 
      name: 'Notion - Update Database', 
      icon: FaDatabase, 
      category: 'data', 
      color: '#000000',
      requiredConnections: ['notion']
    },
    { 
      id: 'google-drive-upload', 
      name: 'Google Drive - Upload File', 
      icon: FaCloudUploadAlt, 
      category: 'data', 
      color: '#34A853',
      requiredConnections: ['google']
    },
    { 
      id: 'google-calendar-event', 
      name: 'Google Calendar - Create Event', 
      icon: FaClock, 
      category: 'data', 
      color: '#34A853',
      requiredConnections: ['google']
    },
    
    // Communication & Messaging
    { 
      id: 'discord-send-message', 
      name: 'Discord - Send Message', 
      icon: FaRobot, 
      category: 'communication', 
      color: '#5865F2',
      requiredConnections: ['discord']
    },
    { 
      id: 'telegram-send-message', 
      name: 'Telegram - Send Message', 
      icon: FaEnvelope, 
      category: 'communication', 
      color: '#0088CC',
      requiredConnections: ['telegram']
    },
    { 
      id: 'whatsapp-send-message', 
      name: 'WhatsApp - Send Message', 
      icon: FaEnvelope, 
      category: 'communication', 
      color: '#25D366',
      requiredConnections: ['whatsapp']
    },
    
    // Analytics & Monitoring
    { 
      id: 'mixpanel-track-event', 
      name: 'Mixpanel - Track Event', 
      icon: FaChartLine, 
      category: 'analytics', 
      color: '#7856FF',
      requiredConnections: ['mixpanel']
    },
    { 
      id: 'segment-track-event', 
      name: 'Segment - Track Event', 
      icon: FaChartLine, 
      category: 'analytics', 
      color: '#52BD94',
      requiredConnections: ['segment']
    },
    
    // E-commerce & Payments
    { 
      id: 'stripe-create-payment', 
      name: 'Stripe - Create Payment', 
      icon: FaCreditCard, 
      category: 'ecommerce', 
      color: '#635BFF',
      requiredConnections: ['stripe']
    },
    { 
      id: 'stripe-create-customer', 
      name: 'Stripe - Create Customer', 
      icon: FaUsers, 
      category: 'ecommerce', 
      color: '#635BFF',
      requiredConnections: ['stripe']
    },
    { 
      id: 'paypal-create-payment', 
      name: 'PayPal - Create Payment', 
      icon: FaCreditCard, 
      category: 'ecommerce', 
      color: '#0070BA',
      requiredConnections: ['paypal']
    },
    { 
      id: 'woocommerce-create-order', 
      name: 'WooCommerce - Create Order', 
      icon: FaShoppingCart, 
      category: 'ecommerce', 
      color: '#96588A',
      requiredConnections: ['woocommerce']
    },
    
    // Triggers for New Services
    { 
      id: 'trigger-rss-feed', 
      name: 'RSS - New Item', 
      icon: FaCode, 
      category: 'trigger', 
      color: '#10B981'
    },
    { 
      id: 'trigger-calendar-event', 
      name: 'Calendar - New Event', 
      icon: FaClock, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['google']
    },
    { 
      id: 'trigger-airtable-record', 
      name: 'Airtable - New Record', 
      icon: FaDatabase, 
      category: 'trigger', 
      color: '#10B981',
      requiredConnections: ['airtable']
    },
    
    // Utility & HTTP
    { 
      id: 'http-request', 
      name: 'HTTP - Send Request', 
      icon: FaCode, 
      category: 'utility', 
      color: '#6B7280'
    },
    { 
      id: 'webhook-send', 
      name: 'Webhook - Send Data', 
      icon: FaCode, 
      category: 'utility', 
      color: '#6B7280'
    },
    { 
      id: 'code-javascript', 
      name: 'Code - JavaScript', 
      icon: FaCode, 
      category: 'utility', 
      color: '#F7DF1E'
    },
    { 
      id: 'code-python', 
      name: 'Code - Python', 
      icon: FaCode, 
      category: 'utility', 
      color: '#3776AB'
    },
    
    // Enhanced Logic
    { id: 'condition-if', name: 'If/Then Condition', icon: FaCodeBranch, category: 'logic', color: '#F59E0B' },
    { id: 'condition-switch', name: 'Switch Case', icon: FaCodeBranch, category: 'logic', color: '#F59E0B' },
    { id: 'condition-filter', name: 'Filter Data', icon: FaFilter, category: 'logic', color: '#F59E0B' },
    { id: 'loop-items', name: 'Loop Through Items', icon: FaCodeBranch, category: 'logic', color: '#F59E0B' },
    
    // Transform & Format
    { id: 'transform-json', name: 'Parse JSON', icon: FaCode, category: 'transform', color: '#8B5CF6' },
    { id: 'transform-csv', name: 'Parse CSV', icon: FaDatabase, category: 'transform', color: '#8B5CF6' },
    { id: 'transform-text', name: 'Format Text', icon: FaEdit, category: 'transform', color: '#8B5CF6' },
    { id: 'transform-date', name: 'Format Date', icon: FaClock, category: 'transform', color: '#8B5CF6' },
    
    // Functions
    { id: 'function-code', name: 'Run Code', icon: FaCode, category: 'function', color: '#8B5CF6' },
    { id: 'function-delay', name: 'Add Delay', icon: FaClock, category: 'function', color: '#8B5CF6' }
  ];

  // Check if a service is connected for the current business
  const isServiceConnected = (service) => {
    if (!currentBusiness) return false;
    
    switch (service) {
      case 'gmail':
      case 'google':
      case 'youtube':
        return authenticatedProviders?.google || false;
      case 'facebook':
      case 'instagram':
        return authenticatedProviders?.facebook || false;
      case 'twitter':
      case 'linkedin':
      case 'slack':
      case 'shopify':
      case 'square':
      case 'toast':
        return serviceConnections[service] || false;
      default:
        return false;
    }
  };

  // Get connection status for a node
  const getNodeConnectionStatus = (nodeType) => {
    const nodeDefinition = workflowNodeTypes.find(n => n.id === nodeType);
    if (!nodeDefinition?.requiredConnections) {
      return { status: 'ready', message: 'No connections required' };
    }

    const missingConnections = nodeDefinition.requiredConnections.filter(
      service => !isServiceConnected(service)
    );

    if (missingConnections.length === 0) {
      return { status: 'connected', message: 'All connections ready' };
    }

    return { 
      status: 'missing', 
      message: `Connect ${missingConnections.join(', ')} to use this node`,
      missingConnections 
    };
  };

  // Auto-detect and update service connections when auth changes
  useEffect(() => {
    if (currentBusiness && authenticatedProviders) {
      setServiceConnections(prev => ({
        ...prev,
        google: authenticatedProviders.google,
        facebook: authenticatedProviders.facebook,
        instagram: authenticatedProviders.facebook, // Instagram uses Facebook login
        gmail: authenticatedProviders.google,
        youtube: authenticatedProviders.google
      }));
    }
  }, [currentBusiness, authenticatedProviders]);

  // Handle connecting a service
  const connectService = async (service) => {
    try {
      // This would trigger the OAuth flow for the specific service
      switch (service) {
        case 'twitter':
          window.open('/auth/twitter', '_blank');
          break;
        case 'linkedin':
          window.open('/auth/linkedin', '_blank');
          break;
        case 'slack':
          window.open('/auth/slack', '_blank');
          break;
        case 'shopify':
          window.open('/auth/shopify', '_blank');
          break;
        case 'square':
          window.open('/auth/square', '_blank');
          break;
        case 'toast':
          window.open('/auth/toast', '_blank');
          break;
        default:
          console.log(`Service ${service} not implemented yet`);
      }
    } catch (error) {
      console.error('Error connecting service:', error);
    }
  };

  // Tutorial steps for new users
  const tutorialSteps = [
    {
      title: "Welcome to Workflow Builder!",
      content: "Create automated workflows for your business. Let's get started with a quick tour.",
      target: null
    },
    {
      title: "Workflow Nodes",
      content: "These are workflow building blocks. Drag them to the canvas to build your automation.",
      target: "node-palette"
    },
    {
      title: "Canvas Area",
      content: "Drop nodes here and connect them to create your workflow. Start with a trigger (green) node.",
      target: "canvas-workspace"
    },
    {
      title: "Connect Nodes",
      content: "Click and drag between node connection points to link your workflow steps together.",
      target: "canvas-workspace"
    },
    {
      title: "Save & Test",
      content: "Use the toolbar to save your workflow and test it. You're ready to automate your business!",
      target: "workflow-toolbar"
    }
  ];
  
  // Chat state
  const [chatSessions, setChatSessions] = useState([
    {
      id: Date.now(),
      title: 'Build a Workflow',
      messages: [],
      timestamp: new Date().toLocaleTimeString()
    }
  ]);
  const [activeChatSession, setActiveChatSession] = useState(chatSessions[0]?.id);
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (initialPrompt) {
      generateInitialStrategy(initialPrompt);
    }
  }, [initialPrompt]);

  const generateInitialStrategy = async (prompt) => {
    setIsGenerating(true);
    
    try {
      const messages = [
        {
          role: 'system',
          content: `You are a strategic business consultant. Based on the user's request, generate a comprehensive business strategy broken down into connected components. 

          Return your response as a JSON object with this structure:
          {
            "centralConcept": "Main business goal/strategy",
            "keyPoints": [
              {
                "title": "Strategy Point Title",
                "description": "Detailed description",
                "type": "strategy|marketing|operations|finance|growth",
                "priority": "high|medium|low",
                "connections": ["index of connected points"]
              }
            ],
            "actionItems": [
              {
                "title": "Actionable step",
                "description": "How to implement",
                "timeline": "timeframe"
              }
            ]
          }`
        },
        {
          role: 'user',
          content: `Generate a comprehensive business strategy for: ${prompt}`
        }
      ];

      const result = await togetherAIService.generateChatResponse(messages, {
        maxTokens: 1200,
        temperature: 0.7
      });

      if (result.success) {
        try {
          const strategyData = JSON.parse(result.response);
          createCanvasFromStrategy(strategyData);
        } catch (e) {
          // Fallback if JSON parsing fails
          createFallbackCanvas(result.response, prompt);
        }
      } else {
        createFallbackCanvas(result.response, prompt);
      }
    } catch (error) {
      console.error('Strategy generation error:', error);
      createFallbackCanvas('Unable to generate strategy. Please try again.', prompt);
    } finally {
      setIsGenerating(false);
    }
  };

  const createCanvasFromStrategy = (strategyData) => {
    const nodes = [];
    const newConnections = [];

    // Adjust positioning based on inline mode
    const centerX = inline ? 500 : 600;
    const centerY = inline ? 250 : 300;
    const radius = inline ? 200 : 280;
    const maxX = inline ? 950 : 1100;
    const maxY = inline ? 450 : 600;

    // Central concept node
    nodes.push({
      id: 'central',
      type: 'central',
      title: strategyData.centralConcept || 'Business Strategy',
      description: 'Core strategic focus',
      x: centerX,
      y: centerY,
      width: 280,
      height: 120
    });

    // Key points as surrounding nodes
    strategyData.keyPoints?.forEach((point, index) => {
      const angle = (index * 360) / strategyData.keyPoints.length;
      const x = centerX + radius * Math.cos(angle * Math.PI / 180);
      const y = centerY + radius * Math.sin(angle * Math.PI / 180);

      nodes.push({
        id: `point-${index}`,
        type: point.type || 'strategy',
        title: point.title,
        description: point.description,
        priority: point.priority,
        x: Math.max(50, Math.min(maxX, x)),
        y: Math.max(50, Math.min(maxY, y)),
        width: 200,
        height: 150
      });

      // Connect to central node
      newConnections.push({
        from: 'central',
        to: `point-${index}`,
        type: 'primary'
      });

      // Connect related points
      point.connections?.forEach(connIndex => {
        if (connIndex < strategyData.keyPoints.length) {
          newConnections.push({
            from: `point-${index}`,
            to: `point-${connIndex}`,
            type: 'secondary'
          });
        }
      });
    });

    // Action items as smaller nodes
    strategyData.actionItems?.forEach((action, index) => {
      const actionY = inline ? 500 : 520;
      const actionSpacing = inline ? 180 : 200;
      nodes.push({
        id: `action-${index}`,
        type: 'action',
        title: action.title,
        description: action.description,
        timeline: action.timeline,
        x: 50 + (index * actionSpacing),
        y: actionY,
        width: 160,
        height: 100
      });
    });

    setCanvasNodes(nodes);
    setConnections(newConnections);
  };

  const createFallbackCanvas = (response, prompt) => {
    // Adjust positioning for inline mode
    const centerX = inline ? 500 : 600;
    const centerY = inline ? 250 : 300;
    
    const nodes = [
      {
        id: 'central',
        type: 'central',
        title: prompt,
        description: 'Your business focus',
        x: centerX,
        y: centerY,
        width: 280,
        height: 120
      },
      {
        id: 'ai-insight',
        type: 'insight',
        title: 'AI Insight',
        description: response.substring(0, 200) + '...',
        x: inline ? 200 : 250,
        y: inline ? 120 : 150,
        width: 220,
        height: 180
      }
    ];

    setCanvasNodes(nodes);
    setConnections([{
      from: 'central',
      to: 'ai-insight',
      type: 'primary'
    }]);
  };

  const getNodeIcon = (type) => {
    const icons = {
      central: FaCrosshairs,
      strategy: FaLightbulb,
      marketing: FaBullhorn,
      operations: FaChartLine,
      finance: FaChartLine,
      growth: FaUsers,
      action: FaPlus,
      insight: FaLightbulb
    };
    return icons[type] || FaLightbulb;
  };

  const getNodeColor = (type, priority) => {
    if (type === 'central') return '#04c0ff';
    
    const colors = {
      strategy: '#9333ea',
      marketing: '#f59e0b',
      operations: '#10b981',
      finance: '#ef4444',
      growth: '#06b6d4',
      action: '#6b7280',
      insight: '#8b5cf6'
    };

    let baseColor = colors[type] || '#6b7280';
    
    if (priority === 'high') {
      return baseColor;
    } else if (priority === 'medium') {
      return baseColor + '80';
    } else {
      return baseColor + '60';
    }
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  // Chat functions
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatSessions]);

  const createNewChatSession = () => {
    // Check if current workflow has nodes
    if (canvasNodes.length > 0) {
      setShowNewWorkflowConfirm(true);
    } else {
      createNewWorkflow();
    }
  };

  const createNewWorkflow = () => {
    const newSession = {
      id: Date.now(),
      title: 'Build a Workflow',
      messages: [],
      timestamp: new Date().toLocaleTimeString()
    };
    setChatSessions([newSession, ...chatSessions]);
    setActiveChatSession(newSession.id);
    
    // Clear canvas
    setCanvasNodes([]);
    setConnections([]);
    setSelectedNode(null);
    setWorkflowName('');
    
    // Close confirmation modal if open
    setShowNewWorkflowConfirm(false);
  };

  const saveCurrentAsTemplate = () => {
    const currentTitle = chatSessions.find(s => s.id === activeChatSession)?.title || 'Build a Workflow';
    setTemplateName(currentTitle);
    setTemplateDescription(`Template created from workflow: ${currentTitle}`);
    setShowNewWorkflowConfirm(false);
    setShowCreateTemplate(true);
  };

  const deleteChatSession = (sessionId) => {
    const updatedSessions = chatSessions.filter(session => session.id !== sessionId);
    setChatSessions(updatedSessions);
    
    if (activeChatSession === sessionId && updatedSessions.length > 0) {
      setActiveChatSession(updatedSessions[0].id);
    }
  };

  // Handle file attachment
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    const type = file.type;
    if (type.startsWith('image/')) return FaImage;
    if (type.startsWith('video/')) return FaVideo;
    if (type.includes('pdf')) return FaFilePdf;
    if (type.includes('word') || type.includes('document')) return FaFileWord;
    if (type.includes('excel') || type.includes('spreadsheet')) return FaFileExcel;
    return FaFile;
  };

  // Workflow generation logic based on user input
  const generateWorkflowFromPrompt = (prompt) => {
    const lowerPrompt = prompt.toLowerCase();
    const nodes = [];
    const connections = [];
    let nodeId = 0;
    
    // Helper function to add a node
    const addNode = (type, x, y, customSettings = {}) => {
      const nodeType = workflowNodeTypes.find(n => n.id === type);
      if (!nodeType) return null;
      
      const node = {
        id: `${type}-${Date.now()}-${nodeId++}`,
        type,
        name: nodeType.name,
        category: nodeType.category,
        color: nodeType.color,
        icon: nodeType.icon,
        x,
        y,
        width: 180,
        height: 80,
        settings: customSettings
      };
      nodes.push(node);
      return node;
    };

    // Helper function to connect nodes
    const connectNodes = (fromNode, toNode) => {
      if (fromNode && toNode) {
        connections.push({
          id: `connection-${Date.now()}-${connections.length}`,
          from: fromNode.id,
          to: toNode.id
        });
      }
    };

    // Workflow generation patterns based on common use cases
    
    // E-commerce workflows
    if (lowerPrompt.includes('order') || lowerPrompt.includes('purchase') || lowerPrompt.includes('payment') || lowerPrompt.includes('ecommerce')) {
      const trigger = addNode('trigger-square-payment', 100, 200);
      const database = addNode('action-database', 300, 200, { table: 'orders', action: 'insert' });
      const email = addNode('action-email', 500, 200, { template: 'order-confirmation' });
      
      connectNodes(trigger, database);
      connectNodes(database, email);
      
      if (lowerPrompt.includes('social') || lowerPrompt.includes('announce')) {
        const social = addNode('action-instagram-post', 700, 200, { postType: 'image' });
        connectNodes(email, social);
      }
    }
    
    // Social media workflows
    else if (lowerPrompt.includes('social') || lowerPrompt.includes('post') || lowerPrompt.includes('instagram') || lowerPrompt.includes('facebook')) {
      if (lowerPrompt.includes('schedule') || lowerPrompt.includes('time')) {
        const trigger = addNode('trigger-schedule', 100, 200);
        const post = addNode('action-instagram-post', 300, 200, { postType: 'image' });
        connectNodes(trigger, post);
      } else {
        const trigger = addNode('trigger-webhook', 100, 200);
        const post = addNode('action-instagram-post', 300, 200, { postType: 'image' });
        connectNodes(trigger, post);
      }
      
      if (lowerPrompt.includes('analytics') || lowerPrompt.includes('track')) {
        const analytics = addNode('action-google-analytics', 500, 200);
        connectNodes(nodes[nodes.length - 1], analytics);
      }
      
      if (lowerPrompt.includes('multiple') || lowerPrompt.includes('all platforms')) {
        const facebook = addNode('action-facebook-post', 300, 300, { postType: 'image' });
        const twitter = addNode('action-twitter-post', 300, 100, { postType: 'text' });
        connectNodes(nodes[0], facebook);
        connectNodes(nodes[0], twitter);
      }
    }
    
    // Email marketing workflows
    else if (lowerPrompt.includes('email') || lowerPrompt.includes('newsletter') || lowerPrompt.includes('campaign')) {
      const trigger = lowerPrompt.includes('new customer') ? 
        addNode('trigger-webhook', 100, 200) : 
        addNode('trigger-schedule', 100, 200);
      
      const email = addNode('action-email', 300, 200, { template: 'newsletter' });
      const database = addNode('action-database', 500, 200, { table: 'email_campaigns', action: 'insert' });
      
      connectNodes(trigger, email);
      connectNodes(email, database);
      
      if (lowerPrompt.includes('follow up')) {
        const followUp = addNode('action-email', 500, 100, { template: 'follow-up' });
        const delay = addNode('logic-delay', 350, 100);
        connectNodes(email, delay);
        connectNodes(delay, followUp);
      }
    }
    
    // Customer support workflows
    else if (lowerPrompt.includes('support') || lowerPrompt.includes('help') || lowerPrompt.includes('ticket')) {
      const trigger = addNode('trigger-email-gmail', 100, 200);
      const classify = addNode('ai-classifier', 300, 200);
      const database = addNode('action-database', 500, 200, { table: 'support_tickets', action: 'insert' });
      const slack = addNode('action-slack', 700, 200);
      
      connectNodes(trigger, classify);
      connectNodes(classify, database);
      connectNodes(database, slack);
      
      if (lowerPrompt.includes('urgent') || lowerPrompt.includes('priority')) {
        const urgentPath = addNode('action-slack', 500, 100);
        connectNodes(classify, urgentPath);
      }
    }
    
    // Lead generation workflows
    else if (lowerPrompt.includes('lead') || lowerPrompt.includes('contact') || lowerPrompt.includes('form')) {
      const trigger = addNode('trigger-webhook', 100, 200);
      const database = addNode('action-database', 300, 200, { table: 'leads', action: 'insert' });
      const email = addNode('action-email', 500, 200, { template: 'welcome' });
      const crm = addNode('action-google-sheets', 700, 200);
      
      connectNodes(trigger, database);
      connectNodes(database, email);
      connectNodes(email, crm);
      
      if (lowerPrompt.includes('nurture') || lowerPrompt.includes('sequence')) {
        const sequence1 = addNode('action-email', 500, 300, { template: 'nurture-1' });
        const sequence2 = addNode('action-email', 700, 300, { template: 'nurture-2' });
        const delay1 = addNode('logic-delay', 350, 300);
        const delay2 = addNode('logic-delay', 600, 300);
        
        connectNodes(email, delay1);
        connectNodes(delay1, sequence1);
        connectNodes(sequence1, delay2);
        connectNodes(delay2, sequence2);
      }
    }
    
    // Content creation workflows
    else if (lowerPrompt.includes('content') || lowerPrompt.includes('blog') || lowerPrompt.includes('article')) {
      const trigger = addNode('trigger-schedule', 100, 200);
      const ai = addNode('ai-content-generator', 300, 200);
      const review = addNode('action-slack', 500, 200);
      const publish = addNode('action-wordpress', 700, 200);
      
      connectNodes(trigger, ai);
      connectNodes(ai, review);
      connectNodes(review, publish);
      
      if (lowerPrompt.includes('social') || lowerPrompt.includes('share')) {
        const social = addNode('action-instagram-post', 700, 300, { postType: 'image' });
        connectNodes(publish, social);
      }
    }
    
    // Default general workflow
    else {
      const trigger = addNode('trigger-webhook', 100, 200);
      const processor = addNode('logic-condition', 300, 200);
      const action1 = addNode('action-email', 500, 150, { template: 'notification' });
      const action2 = addNode('action-database', 500, 250, { action: 'insert' });
      
      connectNodes(trigger, processor);
      connectNodes(processor, action1);
      connectNodes(processor, action2);
    }

    return { nodes, connections };
  };

  const sendChatMessage = async () => {
    if (!messageInput.trim() && selectedFiles.length === 0) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageInput.trim(),
      attachments: selectedFiles.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file)
      })),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    // Update the session with the new message
    const updatedSessions = chatSessions.map(session => {
      if (session.id === activeChatSession) {
        return {
          ...session,
          messages: [...session.messages, userMessage],
          title: session.messages.length === 0 ? 
            messageInput.trim().substring(0, 30) + '...' : session.title,
          timestamp: 'Just now'
        };
      }
      return session;
    });

    setChatSessions(updatedSessions);
    const currentInput = messageInput;
    setMessageInput('');
    setSelectedFiles([]);
    setIsTyping(true);
    setIsGenerating(true);

    try {
      // Generate workflow based on user input
      const generatedWorkflow = generateWorkflowFromPrompt(currentInput);
      
      // Add generated nodes to canvas with a slight delay for visual effect
      setTimeout(() => {
        setCanvasNodes(generatedWorkflow.nodes);
        setConnections(generatedWorkflow.connections);
        setIsGenerating(false);
        
        // Generate AI response about the created workflow
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: `🎯 **Workflow Generated!**

I've created an n8n-style workflow based on your request: "${currentInput}"

**Generated Workflow Includes:**
• ${generatedWorkflow.nodes.length} workflow nodes
• ${generatedWorkflow.connections.length} connections between nodes
• Automated triggers and actions

**Next Steps:**
1. Review the workflow nodes on the canvas
2. Click on any node to configure its settings  
3. Test the workflow connections
4. Save and deploy when ready

The workflow is now displayed on your canvas and ready for customization! Click on any node to see its integration settings in the bottom-right panel.`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        const finalUpdatedSessions = updatedSessions.map(session => {
          if (session.id === activeChatSession) {
            return {
              ...session,
              messages: [...session.messages, botMessage],
              timestamp: 'Just now'
            };
          }
          return session;
        });
        
        setChatSessions(finalUpdatedSessions);
        setIsTyping(false);
      }, 1000);

    } catch (error) {
      console.error('Workflow generation error:', error);
      setIsTyping(false);
      setIsGenerating(false);
      
      // Error message
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: `❌ Sorry, I encountered an error generating your workflow. Please try rephrasing your request or contact support if the issue persists.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      const finalUpdatedSessions = updatedSessions.map(session => {
        if (session.id === activeChatSession) {
          return {
            ...session,
            messages: [...session.messages, errorMessage],
            timestamp: 'Just now'
          };
        }
        return session;
      });
      
      setChatSessions(finalUpdatedSessions);
    }
  };

  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // Title editing functions
  const handleTitleClick = () => {
    const currentTitle = chatSessions.find(s => s.id === activeChatSession)?.title || 'Build a Workflow';
    setTempTitle(currentTitle);
    setIsEditingTitle(true);
  };

  const handleTitleSave = () => {
    if (tempTitle.trim()) {
      // Update the active chat session title
      setChatSessions(sessions => 
        sessions.map(session => 
          session.id === activeChatSession 
            ? { ...session, title: tempTitle.trim() }
            : session
        )
      );
      
      // Also update the workflowName if it's empty, to sync with the title
      if (!workflowName.trim()) {
        setWorkflowName(tempTitle.trim());
      }
    }
    setIsEditingTitle(false);
    setTempTitle('');
  };

  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setTempTitle('');
  };

  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleTitleCancel();
    }
  };

  // Calculate workflow steps order based on connections and spatial positioning
  const calculateWorkflowSteps = () => {
    if (canvasNodes.length === 0) {
      setWorkflowSteps([]);
      return;
    }

    // If there are connections, use connection-based ordering
    if (connections.length > 0) {
      // Find nodes with no incoming connections (start nodes)
      const startNodes = canvasNodes.filter(node => 
        !connections.some(conn => conn.to === node.id)
      );

      // If no clear start nodes but we have connections, pick the leftmost connected node
      if (startNodes.length === 0) {
        const connectedNodeIds = new Set([
          ...connections.map(c => c.from),
          ...connections.map(c => c.to)
        ]);
        const connectedNodes = canvasNodes.filter(node => connectedNodeIds.has(node.id));
        if (connectedNodes.length > 0) {
          startNodes.push(connectedNodes.sort((a, b) => a.x - b.x)[0]);
        }
      }

      if (startNodes.length > 0) {
        // Perform topological sort to determine step order
        const steps = [];
        const visited = new Set();
        const visiting = new Set();

        const visit = (nodeId) => {
          if (visiting.has(nodeId)) return; // Circular dependency
          if (visited.has(nodeId)) return;

          visiting.add(nodeId);

          // Get connected nodes (outgoing connections) sorted by position for consistent ordering
          const outgoingConnections = connections.filter(conn => conn.from === nodeId);
          const sortedOutgoing = outgoingConnections
            .map(conn => ({ conn, node: canvasNodes.find(n => n.id === conn.to) }))
            .filter(item => item.node)
            .sort((a, b) => a.node.x - b.node.x)
            .map(item => item.conn);
          
          sortedOutgoing.forEach(conn => {
            visit(conn.to);
          });

          visiting.delete(nodeId);
          visited.add(nodeId);

          // Add node to steps (reverse order due to DFS)
          const node = canvasNodes.find(n => n.id === nodeId);
          if (node) {
            steps.unshift(node);
          }
        };

        // Start from all start nodes (sorted by position for consistency)
        startNodes.sort((a, b) => a.x - b.x).forEach(startNode => {
          visit(startNode.id);
        });

        // Add any remaining unvisited nodes sorted by position
        const unvisitedNodes = canvasNodes
          .filter(node => !visited.has(node.id))
          .sort((a, b) => a.x - b.x);
        
        steps.push(...unvisitedNodes);
        setWorkflowSteps(steps);
        return;
      }
    }

    // Fallback: No connections or no clear start, sort by spatial position
    // Use a combination of Y position (top to bottom) and X position (left to right)
    const sortedByPosition = [...canvasNodes].sort((a, b) => {
      const yDiff = a.y - b.y;
      // If nodes are roughly at the same Y level (within 50px), sort by X
      if (Math.abs(yDiff) < 50) {
        return a.x - b.x;
      }
      return yDiff;
    });

    setWorkflowSteps(sortedByPosition);
  };

  // Update workflow steps when nodes or connections change
  React.useEffect(() => {
    calculateWorkflowSteps();
  }, [canvasNodes, connections]);

  // Handle step button click
  const handleStepClick = (stepIndex) => {
    // Toggle highlighting - if same step clicked, clear highlight
    if (highlightedStep === stepIndex) {
      setHighlightedStep(null);
      return;
    }
    
    setHighlightedStep(stepIndex);
    const step = workflowSteps[stepIndex];
    if (step) {
      // Center the canvas on the highlighted step
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        // Scroll to center the node (if scrollable)
        canvas.scrollTo({
          left: step.x - centerX + step.width / 2,
          top: step.y - centerY + step.height / 2,
          behavior: 'smooth'
        });
      }
    }
  };

  // Clear highlighting when clicking on canvas
  const handleCanvasClick = (e) => {
    if (e.target === canvasRef.current) {
      setHighlightedStep(null);
      setSelectedNode(null);
    }
  };

  // Track drag state to distinguish from click
  const [isDragActive, setIsDragActive] = useState(false);
  
  // Force re-render when workflow nodes change
  const [workflowNodesVersion, setWorkflowNodesVersion] = useState(0);
  
  // Update workflow nodes version when canvas changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.flowCanvasAPI) {
        setWorkflowNodesVersion(prev => prev + 1);
      }
    }, 2000); // Check every 2 seconds for node changes
    
    return () => clearInterval(interval);
  }, []);

  // Drag and drop handlers
  const handleNodeDragStart = (e, nodeType) => {
    setIsDragActive(true);
    
    // Set data for ReactFlow canvas
    const nodeData = {
      type: nodeType.id,
      label: nodeType.name,
      category: nodeType.category,
      icon: nodeType.icon,
      description: nodeType.description || '',
      subtitle: nodeType.subtitle || '',
      hasInput: nodeType.category !== 'trigger',
      hasOutput: true,
      requiresConnection: nodeType.requiredConnections && nodeType.requiredConnections.length > 0,
      connected: nodeType.requiredConnections ? 
        nodeType.requiredConnections.every(service => isServiceConnected(service)) : 
        true
    };
    
    e.dataTransfer.setData('application/reactflow', JSON.stringify(nodeData));
    
    setDraggedNode(nodeType);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleNodeDragEnd = () => {
    setIsDragActive(false);
    setDraggedNode(null);
    setIsDragging(false);
  };

  // Handle clicking on palette nodes to add them to canvas
  const handlePaletteNodeClick = (nodeType) => {
    // Don't trigger click if user is dragging
    if (isDragActive) {
      return;
    }
    
    // Small delay to allow drag detection
    setTimeout(() => {
      if (isDragActive) return;
      
        // Create the same node data as drag and drop
      const nodeData = {
        type: nodeType.id,
        label: nodeType.name,
        category: nodeType.category,
        icon: nodeType.icon,
        description: nodeType.description || '',
        subtitle: nodeType.subtitle || '',
        hasInput: nodeType.category !== 'trigger',
        hasOutput: true,
        requiresConnection: nodeType.requiredConnections && nodeType.requiredConnections.length > 0,
        connected: nodeType.requiredConnections ? 
          nodeType.requiredConnections.every(service => isServiceConnected(service)) : 
          true
      };

      // Use FlowCanvas API to add node
      if (window.flowCanvasAPI && window.flowCanvasAPI.addNode) {
        window.flowCanvasAPI.addNode(nodeData);
        
        // Visual feedback - briefly highlight the node that was clicked
        const clickedElement = document.querySelector(`[data-node-id="${nodeType.id}"]`);
        if (clickedElement) {
          clickedElement.style.transform = 'scale(0.95)';
          setTimeout(() => {
            clickedElement.style.transform = '';
          }, 150);
        }
      }
    }, 100); // 100ms delay to distinguish from drag
  };

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    if (!draggedNode) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - 90; // Center the node
    const y = e.clientY - rect.top - 50;

    const newNode = {
      id: `${draggedNode.id}-${Date.now()}`,
      type: draggedNode.id,
      name: draggedNode.name,
      category: draggedNode.category,
      color: draggedNode.color,
      icon: draggedNode.icon,
      x: x,
      y: y,
      width: 180,
      height: 100
    };

    // Add the new node
    setCanvasNodes(prev => [...prev, newNode]);
    
    // Auto-connect to workflow chain
    autoConnectNode(newNode, x, y);
    
    setDraggedNode(null);
    setIsDragging(false);

    // Close tutorial after first node drop
    if (canvasNodes.length === 0 && showTutorial) {
      setShowTutorial(false);
    }
  };

  // Automatically connect new node to the workflow chain
  const autoConnectNode = (newNode, dropX, dropY) => {
    if (canvasNodes.length === 0) return; // First node, no connections needed
    
    // Find the closest node to connect from/to based on position
    const closestNode = findClosestNodeForConnection(dropX, dropY);
    
    if (closestNode) {
      // Determine if new node should be connected before or after the closest node
      const shouldConnectAfter = dropX > closestNode.x + closestNode.width / 2;
      
      if (shouldConnectAfter) {
        // Insert new node after the closest node
        insertNodeInChain(closestNode.id, newNode.id, 'after');
      } else {
        // Insert new node before the closest node
        insertNodeInChain(closestNode.id, newNode.id, 'before');
      }
    } else {
      // If no suitable node found, connect to the last node in the chain
      const lastNode = findLastNodeInChain();
      if (lastNode) {
        setConnections(prev => [...prev, {
          from: lastNode.id,
          to: newNode.id,
          type: 'primary'
        }]);
      }
    }
  };

  // Find the closest node for connection based on position
  const findClosestNodeForConnection = (x, y) => {
    let closestNode = null;
    let minDistance = Infinity;
    
    canvasNodes.forEach(node => {
      const nodeCenterX = node.x + node.width / 2;
      const nodeCenterY = node.y + node.height / 2;
      const distance = Math.sqrt((x - nodeCenterX) ** 2 + (y - nodeCenterY) ** 2);
      
      if (distance < minDistance && distance < 200) { // Within 200px proximity
        minDistance = distance;
        closestNode = node;
      }
    });
    
    return closestNode;
  };

  // Check connection proximity and show preview
  const checkConnectionProximity = (nodeId, x, y) => {
    const nodeCenterX = x + 90; // Half of node width (180px / 2)
    const nodeCenterY = y + 50; // Half of node height (100px / 2)
    
    // Find nearby nodes for potential connections
    const nearbyNodes = canvasNodes.filter(node => {
      if (node.id === nodeId) return false; // Skip the dragged node
      
      const targetCenterX = node.x + 90;
      const targetCenterY = node.y + 50;
      const distance = Math.sqrt(
        (nodeCenterX - targetCenterX) ** 2 + (nodeCenterY - targetCenterY) ** 2
      );
      
      return distance < 150; // Show preview within 150px
    });
    
    // Show connection preview for nearest node
    if (nearbyNodes.length > 0) {
      const nearestNode = nearbyNodes.reduce((closest, node) => {
        const targetCenterX = node.x + 90;
        const targetCenterY = node.y + 50;
        const distance = Math.sqrt(
          (nodeCenterX - targetCenterX) ** 2 + (nodeCenterY - targetCenterY) ** 2
        );
        
        if (!closest.distance || distance < closest.distance) {
          return { node, distance };
        }
        return closest;
      }, {});
      
      // Set connection preview state
      setConnectionPreview({
        show: true,
        from: { x: nodeCenterX, y: nodeCenterY },
        to: { 
          x: nearestNode.node.x + 90, 
          y: nearestNode.node.y + 50 
        },
        nodeId: nearestNode.node.id
      });
    } else {
      // Hide preview when no nearby nodes
      setConnectionPreview({ show: false });
    }
  };

  // Insert a node in the workflow chain
  const insertNodeInChain = (referenceNodeId, newNodeId, position) => {
    setConnections(prev => {
      const updated = [...prev];
      
      if (position === 'after') {
        // Find connections where referenceNode is the source
        const outgoingConnections = updated.filter(c => c.from === referenceNodeId);
        
        if (outgoingConnections.length > 0) {
          // Reroute existing connections through the new node
          outgoingConnections.forEach(conn => {
            const index = updated.indexOf(conn);
            updated[index] = { ...conn, from: newNodeId };
          });
          
          // Add connection from reference to new node
          updated.push({
            from: referenceNodeId,
            to: newNodeId,
            type: 'primary'
          });
        } else {
          // No outgoing connections, just add new connection
          updated.push({
            from: referenceNodeId,
            to: newNodeId,
            type: 'primary'
          });
        }
      } else { // before
        // Find connections where referenceNode is the destination
        const incomingConnections = updated.filter(c => c.to === referenceNodeId);
        
        if (incomingConnections.length > 0) {
          // Reroute existing connections through the new node
          incomingConnections.forEach(conn => {
            const index = updated.indexOf(conn);
            updated[index] = { ...conn, to: newNodeId };
          });
          
          // Add connection from new node to reference
          updated.push({
            from: newNodeId,
            to: referenceNodeId,
            type: 'primary'
          });
        } else {
          // No incoming connections, just add new connection
          updated.push({
            from: newNodeId,
            to: referenceNodeId,
            type: 'primary'
          });
        }
      }
      
      return updated;
    });
  };

  // Find the last node in the workflow chain
  const findLastNodeInChain = () => {
    // Find nodes with no outgoing connections
    const endNodes = canvasNodes.filter(node => {
      const hasOutgoing = connections.some(c => c.from === node.id);
      return !hasOutgoing;
    });
    
    // Return the rightmost end node (assuming left-to-right workflow)
    return endNodes.reduce((rightmost, node) => {
      if (!rightmost || node.x > rightmost.x) {
        return node;
      }
      return rightmost;
    }, null);
  };

  // Check for auto-connection when a node is moved near another
  const checkForAutoConnection = (draggedNodeId, nodeX, nodeY) => {
    const draggedNode = canvasNodes.find(n => n.id === draggedNodeId);
    if (!draggedNode) return;
    
    const SNAP_DISTANCE = 100; // Distance in pixels to trigger auto-connection
    
    canvasNodes.forEach(targetNode => {
      if (targetNode.id === draggedNodeId) return;
      
      // Calculate distance between nodes
      const distanceX = Math.abs((nodeX + draggedNode.width / 2) - (targetNode.x + targetNode.width / 2));
      const distanceY = Math.abs((nodeY + draggedNode.height / 2) - (targetNode.y + targetNode.height / 2));
      
      // Check if nodes are close enough for auto-connection
      if (distanceX < SNAP_DISTANCE && distanceY < SNAP_DISTANCE) {
        // Check if connection already exists
        const connectionExists = connections.some(c => 
          (c.from === draggedNodeId && c.to === targetNode.id) ||
          (c.from === targetNode.id && c.to === draggedNodeId)
        );
        
        if (!connectionExists) {
          // Determine connection direction based on relative positions
          if (nodeX > targetNode.x + targetNode.width) {
            // Dragged node is to the right, connect from target to dragged
            createSmartConnection(targetNode.id, draggedNodeId);
          } else if (nodeX + draggedNode.width < targetNode.x) {
            // Dragged node is to the left, connect from dragged to target
            createSmartConnection(draggedNodeId, targetNode.id);
          }
        }
      }
    });
  };

  // Create a smart connection that maintains workflow order
  const createSmartConnection = (fromId, toId) => {
    setConnections(prev => {
      // Check if adding this connection would create a cycle
      if (wouldCreateCycle(fromId, toId, prev)) {
        console.log('Connection would create a cycle, skipping');
        return prev;
      }
      
      // Check if the target node already has an incoming connection from the same direction
      const targetHasIncoming = prev.some(c => c.to === toId);
      
      if (targetHasIncoming) {
        // If target already has incoming, don't add another unless it's replacing
        const sourceHasOutgoing = prev.some(c => c.from === fromId);
        
        if (!sourceHasOutgoing) {
          // Source doesn't have outgoing, safe to add
          return [...prev, { from: fromId, to: toId, type: 'primary' }];
        }
        
        return prev; // Both have connections, don't auto-connect
      }
      
      // Safe to add the connection
      return [...prev, { from: fromId, to: toId, type: 'primary' }];
    });
  };

  // Check if adding a connection would create a cycle
  const wouldCreateCycle = (fromId, toId, existingConnections) => {
    // Build adjacency list
    const adjacency = {};
    existingConnections.forEach(conn => {
      if (!adjacency[conn.from]) adjacency[conn.from] = [];
      adjacency[conn.from].push(conn.to);
    });
    
    // Add the proposed connection
    if (!adjacency[fromId]) adjacency[fromId] = [];
    adjacency[fromId].push(toId);
    
    // Check for cycle using DFS
    const visited = new Set();
    const recursionStack = new Set();
    
    const hasCycle = (node) => {
      visited.add(node);
      recursionStack.add(node);
      
      const neighbors = adjacency[node] || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          if (hasCycle(neighbor)) return true;
        } else if (recursionStack.has(neighbor)) {
          return true;
        }
      }
      
      recursionStack.delete(node);
      return false;
    };
    
    // Check from the source node
    return hasCycle(fromId);
  };

  // Tutorial handlers
  const nextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      setShowTutorial(false);
    }
  };

  const skipTutorial = () => {
    setShowTutorial(false);
  };

  const expandNode = async (nodeId) => {
    const node = canvasNodes.find(n => n.id === nodeId);
    if (!node) return;

    setIsGenerating(true);
    try {
      const messages = [
        {
          role: 'system',
          content: 'You are a business strategist. Provide 3-4 specific, actionable sub-strategies or tactics for the given business concept. Keep each point concise but detailed.'
        },
        {
          role: 'user',
          content: `Expand on this business strategy point: "${node.title}" - ${node.description}`
        }
      ];

      const result = await togetherAIService.generateChatResponse(messages, {
        maxTokens: 400,
        temperature: 0.6
      });

      if (result.success) {
        // Create sub-nodes around the expanded node
        const subPoints = result.response.split('\n').filter(line => line.trim().length > 0).slice(0, 4);
        const newNodes = [];
        const newConnections = [];

        subPoints.forEach((point, index) => {
          const angle = (index * 90) - 45;
          const radius = 150;
          const x = node.x + radius * Math.cos(angle * Math.PI / 180);
          const y = node.y + radius * Math.sin(angle * Math.PI / 180);

          const subNodeId = `${nodeId}-sub-${index}`;
          newNodes.push({
            id: subNodeId,
            type: 'expansion',
            title: point.substring(0, 30) + '...',
            description: point,
            x: Math.max(20, Math.min(800, x)),
            y: Math.max(20, Math.min(580, y)),
            width: 140,
            height: 100
          });

          newConnections.push({
            from: nodeId,
            to: subNodeId,
            type: 'expansion'
          });
        });

        setCanvasNodes(prev => [...prev, ...newNodes]);
        setConnections(prev => [...prev, ...newConnections]);
      }
    } catch (error) {
      console.error('Node expansion error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle node dragging on canvas
  const handleNodeMouseDown = (e, nodeId) => {
    e.preventDefault();
    e.stopPropagation();
    
    const node = canvasNodes.find(n => n.id === nodeId);
    if (!node) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const offsetX = e.clientX - node.x - rect.left;
    const offsetY = e.clientY - node.y - rect.top;
    
    setIsDraggingNode(true);
    setDraggedNodeId(nodeId);
    setSelectedNode(node);

    const handleMouseMove = (e) => {
      const newX = e.clientX - rect.left - offsetX;
      const newY = e.clientY - rect.top - offsetY;
      
      // Constrain to canvas bounds
      const maxX = rect.width - node.width;
      const maxY = rect.height - node.height;
      
      const constrainedX = Math.max(0, Math.min(maxX, newX));
      const constrainedY = Math.max(0, Math.min(maxY, newY));

      setCanvasNodes(prev => prev.map(n => 
        n.id === nodeId 
          ? { 
              ...n, 
              x: constrainedX, 
              y: constrainedY
            }
          : n
      ));
      
      // Check for potential connections and show preview
      checkConnectionProximity(nodeId, constrainedX, constrainedY);
    };

    const handleMouseUp = (e) => {
      // Check for auto-connection when dropping near another node
      if (nodeId && node) {
        checkForAutoConnection(nodeId, node.x, node.y);
      }
      
      // Clear connection preview
      setConnectionPreview({ show: false });
      
      setIsDraggingNode(false);
      setDraggedNodeId(null);
      
      // Recalculate workflow steps after node movement
      setTimeout(() => {
        calculateWorkflowSteps();
      }, 100);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Workflow templates
  const workflowTemplates = [
    {
      id: 'social-scheduler',
      name: 'Social Media Scheduler',
      description: 'Automatically post content across all social platforms',
      nodes: [
        { type: 'trigger-schedule', x: 100, y: 200 },
        { type: 'ai-text-generate', x: 300, y: 200 },
        { type: 'ai-image-generate', x: 500, y: 100 },
        { type: 'action-instagram-post', x: 700, y: 150 },
        { type: 'action-facebook-post', x: 700, y: 250 },
        { type: 'action-twitter-post', x: 700, y: 350 }
      ]
    },
    {
      id: 'customer-onboarding',
      name: 'Customer Onboarding',
      description: 'Automated welcome flow for new customers',
      nodes: [
        { type: 'trigger-form', x: 100, y: 200 },
        { type: 'action-email', x: 300, y: 200 },
        { type: 'function-delay', x: 500, y: 200 },
        { type: 'action-slack', x: 700, y: 200 }
      ]
    },
    {
      id: 'content-creator',
      name: 'Content Creator AI',
      description: 'Generate and post AI-powered content',
      nodes: [
        { type: 'trigger-schedule', x: 100, y: 200 },
        { type: 'ai-text-generate', x: 300, y: 150 },
        { type: 'ai-image-generate', x: 300, y: 250 },
        { type: 'ai-video-generate', x: 300, y: 350 },
        { type: 'action-youtube-upload', x: 500, y: 300 }
      ]
    },
    {
      id: 'analytics-reporter',
      name: 'Analytics Reporter',
      description: 'Pull data from Google Analytics and create reports',
      nodes: [
        { type: 'trigger-schedule', x: 100, y: 200 },
        { type: 'action-google-analytics', x: 300, y: 200 },
        { type: 'ai-text-generate', x: 500, y: 200 },
        { type: 'action-email', x: 700, y: 200 }
      ]
    }
  ];

  const loadTemplate = (template) => {
    const newNodes = template.nodes.map((node, index) => {
      const nodeType = workflowNodeTypes.find(wn => wn.id === node.type);
      return {
        id: `${node.type}-${Date.now()}-${index}`,
        type: node.type,
        name: nodeType?.name || 'Unknown',
        category: nodeType?.category || 'action',
        color: nodeType?.color || '#6B7280',
        icon: nodeType?.icon || FaLightbulb,
        x: node.x,
        y: node.y,
        width: 180,
        height: 100,
        settings: {}
      };
    });

    // Create connections between consecutive nodes
    const newConnections = [];
    for (let i = 0; i < newNodes.length - 1; i++) {
      newConnections.push({
        from: newNodes[i].id,
        to: newNodes[i + 1].id,
        type: 'primary'
      });
    }

    setCanvasNodes(newNodes);
    setConnections(newConnections);
    setShowTemplates(false);
  };

  // Load custom templates from localStorage
  useEffect(() => {
    const savedTemplates = localStorage.getItem('customWorkflowTemplates');
    if (savedTemplates) {
      setCustomTemplates(JSON.parse(savedTemplates));
    }
  }, []);

  // Save custom template
  const saveCustomTemplate = () => {
    if (!templateName.trim() || canvasNodes.length === 0) return;

    const newTemplate = {
      id: `custom-${Date.now()}`,
      name: templateName.trim(),
      description: templateDescription.trim() || 'Custom workflow template',
      category: workflowCategory,
      isCustom: true,
      createdAt: new Date().toISOString(),
      nodes: canvasNodes.map(node => ({
        type: node.type,
        x: node.x,
        y: node.y,
        settings: node.settings || {}
      })),
      connections: connections
    };

    const updatedTemplates = [...customTemplates, newTemplate];
    setCustomTemplates(updatedTemplates);
    localStorage.setItem('customWorkflowTemplates', JSON.stringify(updatedTemplates));

    // Reset form
    setTemplateName('');
    setTemplateDescription('');
    setWorkflowCategory('general');
    setShowCreateTemplate(false);
    
    // Success message
    alert(`Template "${templateName.trim()}" saved successfully in ${workflowCategory} category!`);
  };

  // Delete custom template
  const deleteCustomTemplate = (templateId) => {
    const updatedTemplates = customTemplates.filter(t => t.id !== templateId);
    setCustomTemplates(updatedTemplates);
    localStorage.setItem('customWorkflowTemplates', JSON.stringify(updatedTemplates));
  };

  // Load custom template
  const loadCustomTemplate = (template) => {
    const newNodes = template.nodes.map((node, index) => {
      const nodeType = workflowNodeTypes.find(wn => wn.id === node.type);
      return {
        id: `${node.type}-${Date.now()}-${index}`,
        type: node.type,
        name: nodeType?.name || 'Unknown',
        category: nodeType?.category || 'action',
        color: nodeType?.color || '#6B7280',
        icon: nodeType?.icon || FaLightbulb,
        x: node.x,
        y: node.y,
        width: 150,
        height: 80,
        settings: node.settings || {}
      };
    });

    // Recreate connections with new node IDs
    const newConnections = [];
    if (template.connections) {
      template.connections.forEach(conn => {
        const fromIndex = template.nodes.findIndex(n => n.id === conn.from || template.nodes.indexOf(template.nodes.find(tn => tn.type === conn.from)) !== -1);
        const toIndex = template.nodes.findIndex(n => n.id === conn.to || template.nodes.indexOf(template.nodes.find(tn => tn.type === conn.to)) !== -1);
        
        if (fromIndex !== -1 && toIndex !== -1 && newNodes[fromIndex] && newNodes[toIndex]) {
          newConnections.push({
            from: newNodes[fromIndex].id,
            to: newNodes[toIndex].id,
            type: conn.type || 'primary'
          });
        }
      });
    }

    setCanvasNodes(newNodes);
    setConnections(newConnections);
    setShowTemplates(false);
  };

  // Download workflow output in specified format
  const downloadWorkflowOutput = async (format) => {
    if (!workflowOutput) return;
    
    try {
      let blob;
      let filename = workflowOutput.name || `workflow-output-${Date.now()}`;
      
      switch (format) {
        case 'original':
          // Download in original format
          if (workflowOutput.data instanceof Blob) {
            blob = workflowOutput.data;
          } else if (typeof workflowOutput.data === 'string') {
            // Assume it's a data URL
            const response = await fetch(workflowOutput.data);
            blob = await response.blob();
          }
          break;
          
        case 'png':
          if (workflowOutput.type === 'image') {
            // Convert to PNG if not already
            blob = await convertImageToFormat(workflowOutput.data, 'png');
            filename = filename.replace(/\.[^/.]+$/, '') + '.png';
          }
          break;
          
        case 'jpg':
          if (workflowOutput.type === 'image') {
            // Convert to JPG
            blob = await convertImageToFormat(workflowOutput.data, 'jpg');
            filename = filename.replace(/\.[^/.]+$/, '') + '.jpg';
          }
          break;
          
        case 'mp4':
          if (workflowOutput.type === 'video') {
            // Ensure MP4 format
            blob = workflowOutput.data;
            filename = filename.replace(/\.[^/.]+$/, '') + '.mp4';
          }
          break;
          
        case 'gif':
          if (workflowOutput.type === 'video') {
            // Convert video to GIF (simplified - would need proper video processing)
            console.log('GIF conversion would require video processing library');
            blob = workflowOutput.data;
            filename = filename.replace(/\.[^/.]+$/, '') + '.gif';
          }
          break;
      }
      
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading output:', error);
    }
  };

  // Convert image to different format
  const convertImageToFormat = (imageData, format) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob(resolve, `image/${format === 'jpg' ? 'jpeg' : format}`);
      };
      
      if (typeof imageData === 'string') {
        img.src = imageData;
      } else {
        img.src = URL.createObjectURL(imageData);
      }
    });
  };

  // Save output to cloud storage
  const saveToCloud = async () => {
    if (!workflowOutput) return;
    
    try {
      // This would integrate with cloud storage services
      console.log('Saving to cloud storage...', workflowOutput);
      
      // Simulate cloud upload
      const uploadData = {
        name: workflowOutput.name || `workflow-output-${Date.now()}`,
        type: workflowOutput.type,
        data: workflowOutput.data,
        uploadedAt: new Date().toISOString(),
        workflowId: `workflow-${Date.now()}`
      };
      
      // Save reference to localStorage (in real app, this would be cloud API)
      const cloudUploads = JSON.parse(localStorage.getItem('cloudUploads') || '[]');
      cloudUploads.push(uploadData);
      localStorage.setItem('cloudUploads', JSON.stringify(cloudUploads));
      
      alert('Output saved to cloud storage successfully!');
    } catch (error) {
      console.error('Error saving to cloud:', error);
      alert('Failed to save to cloud storage');
    }
  };

  // Share output with others
  const shareOutput = async () => {
    if (!workflowOutput) return;
    
    try {
      if (navigator.share && workflowOutput.data instanceof File) {
        // Use native Web Share API if available
        await navigator.share({
          title: 'Workflow Output',
          text: `Check out this output from my workflow: ${workflowOutput.name}`,
          files: [workflowOutput.data]
        });
      } else {
        // Fallback: copy link to clipboard
        const shareUrl = URL.createObjectURL(workflowOutput.data);
        await navigator.clipboard.writeText(shareUrl);
        alert('Share link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing output:', error);
      // Fallback: show share options
      const shareText = `Check out this workflow output: ${workflowOutput.name}`;
      const encodedText = encodeURIComponent(shareText);
      const shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
      window.open(shareUrl, '_blank');
    }
  };

  // Save workflow with name and category
  const saveWorkflow = () => {
    if (!workflowName.trim() || canvasNodes.length === 0) return;

    const workflow = {
      id: `workflow-${Date.now()}`,
      name: workflowName.trim(),
      category: workflowCategory,
      createdAt: new Date().toISOString(),
      nodes: canvasNodes.map(node => ({
        type: node.type,
        x: node.x,
        y: node.y,
        settings: nodeSettings[node.id] || {}
      })),
      connections: connections,
      output: workflowOutput ? {
        type: workflowOutput.type,
        name: workflowOutput.name,
        createdAt: new Date().toISOString()
      } : null
    };

    // Save to localStorage
    const savedWorkflows = JSON.parse(localStorage.getItem('savedWorkflows') || '[]');
    savedWorkflows.push(workflow);
    localStorage.setItem('savedWorkflows', JSON.stringify(savedWorkflows));

    // If saving as template
    if (workflow.category !== 'personal') {
      const newTemplate = {
        ...workflow,
        id: `custom-${Date.now()}`,
        description: `Saved workflow from ${new Date().toLocaleDateString()}`,
        isCustom: true
      };
      
      const updatedTemplates = [...customTemplates, newTemplate];
      setCustomTemplates(updatedTemplates);
      localStorage.setItem('customWorkflowTemplates', JSON.stringify(updatedTemplates));
    }

    // Reset form
    setWorkflowName('');
    setWorkflowCategory('general');
    setShowSaveWorkflow(false);
    
    // Success message
    alert(`Workflow "${workflowName}" saved successfully!`);
  };

  // Export workflow in different formats
  const exportWorkflow = (format) => {
    const workflowData = {
      name: workflowName || 'Untitled Workflow',
      created: new Date().toISOString(),
      nodes: canvasNodes,
      connections: connections,
      settings: nodeSettings
    };

    switch (format) {
      case 'json':
        const jsonBlob = new Blob([JSON.stringify(workflowData, null, 2)], { type: 'application/json' });
        downloadFile(jsonBlob, `${workflowData.name}.json`);
        break;
        
      case 'n8n':
        const n8nFormat = convertToN8nFormat(workflowData);
        const n8nBlob = new Blob([JSON.stringify(n8nFormat, null, 2)], { type: 'application/json' });
        downloadFile(n8nBlob, `${workflowData.name}_n8n.json`);
        break;
        
      case 'zapier':
        const zapierFormat = convertToZapierFormat(workflowData);
        const zapierBlob = new Blob([JSON.stringify(zapierFormat, null, 2)], { type: 'application/json' });
        downloadFile(zapierBlob, `${workflowData.name}_zapier.json`);
        break;
        
      case 'image':
        exportAsImage();
        break;
        
      default:
        break;
    }
    
    setShowExportOptions(false);
  };

  // Helper function to download files
  const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Convert to n8n format
  const convertToN8nFormat = (workflowData) => {
    return {
      name: workflowData.name,
      nodes: workflowData.nodes.map(node => ({
        name: node.name,
        type: mapToN8nNodeType(node.type),
        position: [node.x, node.y],
        parameters: node.settings || {}
      })),
      connections: workflowData.connections
    };
  };

  // Convert to Zapier format
  const convertToZapierFormat = (workflowData) => {
    return {
      name: workflowData.name,
      steps: workflowData.nodes.map((node, index) => ({
        id: index + 1,
        app: mapToZapierApp(node.type),
        action: node.name,
        settings: node.settings || {}
      }))
    };
  };

  // Map node types to n8n/Zapier equivalents
  const mapToN8nNodeType = (type) => {
    const mapping = {
      'trigger-email': 'n8n-nodes-base.emailReadImap',
      'action-email': 'n8n-nodes-base.emailSend',
      'action-slack': 'n8n-nodes-base.slack',
      // Add more mappings as needed
    };
    return mapping[type] || 'n8n-nodes-base.function';
  };

  const mapToZapierApp = (type) => {
    const mapping = {
      'trigger-email': 'gmail',
      'action-email': 'gmail',
      'action-slack': 'slack',
      // Add more mappings as needed
    };
    return mapping[type] || 'webhooks';
  };

  // Export workflow as image
  const exportAsImage = async () => {
    if (!canvasRef.current) return;
    
    try {
      const html2canvas = await import('html2canvas');
      const canvas = await html2canvas.default(canvasRef.current);
      canvas.toBlob((blob) => {
        downloadFile(blob, `${workflowName || 'workflow'}.png`);
      });
    } catch (error) {
      console.error('Error exporting as image:', error);
    }
  };

  // Handle workflow output (images, videos, files)
  const handleWorkflowOutput = (output) => {
    setWorkflowOutput(output);
    setShowOutputViewer(true);

    // Detect output type and handle accordingly
    if (output.type === 'image') {
      openInDefaultApp(output.data, 'image');
    } else if (output.type === 'video') {
      openInDefaultApp(output.data, 'video');
    } else if (output.type === 'file') {
      openInDefaultApp(output.data, 'file');
    }
  };

  // Function to get workflow nodes in order (from start to end)
  const getWorkflowNodesInOrder = () => {
    const nodes = window.flowCanvasAPI ? window.flowCanvasAPI.getNodes() : [];
    const edges = window.flowCanvasAPI ? window.flowCanvasAPI.getEdges() : [];
    
    if (nodes.length === 0) return [];
    
    // Find trigger/start nodes (nodes without incoming edges)
    const startNodes = nodes.filter(node => 
      !edges.some(edge => edge.target === node.id)
    );
    
    // If no start node, just return nodes sorted by position
    if (startNodes.length === 0) {
      return nodes.sort((a, b) => a.position.x - b.position.x);
    }
    
    // Build workflow chain from start nodes
    const orderedNodes = [];
    const visited = new Set();
    
    const traverseWorkflow = (nodeId) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        orderedNodes.push(node);
        
        // Find next nodes connected to this one
        const outgoingEdges = edges.filter(edge => edge.source === nodeId);
        outgoingEdges.forEach(edge => {
          traverseWorkflow(edge.target);
        });
      }
    };
    
    // Start traversal from each start node
    startNodes.forEach(node => traverseWorkflow(node.id));
    
    return orderedNodes;
  };

  // Function to select a node by its workflow position
  const selectWorkflowNode = (nodeIndex) => {
    const orderedNodes = getWorkflowNodesInOrder();
    if (nodeIndex < orderedNodes.length) {
      const nodeToSelect = orderedNodes[nodeIndex];
      
      // Use FlowCanvas API to focus on the node (which also selects it)
      if (window.flowCanvasAPI && window.flowCanvasAPI.focusNode) {
        window.flowCanvasAPI.focusNode(nodeToSelect.id);
      } else {
        // Fallback selection
        setSelectedNode(nodeToSelect);
      }
    }
  };

  // === ENHANCED WORKFLOW EXECUTION FUNCTIONS ===
  const runWorkflow = async () => {
    // Get nodes and edges from FlowCanvas
    const nodes = window.flowCanvasAPI ? window.flowCanvasAPI.getNodes() : [];
    const edges = window.flowCanvasAPI ? window.flowCanvasAPI.getEdges() : [];
    const nodeConfigurations = window.flowCanvasAPI ? window.flowCanvasAPI.getNodeConfigurations() : new Map();
    
    if (nodes.length === 0) {
      alert('Please add nodes to your workflow before running');
      return;
    }

    setIsExecuting(true);
    setExecutedSteps(new Set());
    setCurrentExecutingStep(null);
    
    try {
      // Calculate execution order based on ReactFlow nodes and edges
      const executionOrder = calculateExecutionOrder(nodes, edges);
      
      if (executionOrder.length === 0) {
        alert('No valid workflow found. Make sure nodes are connected.');
        setIsExecuting(false);
        return;
      }
      
      // Execute each step sequentially
      for (let i = 0; i < executionOrder.length; i++) {
        const currentNode = executionOrder[i];
        setCurrentExecutingStep(currentNode.id);
        
        // Get node configuration
        const config = nodeConfigurations.get(currentNode.id) || {};
        
        // Execute the specific node
        await executeNode(currentNode, config);
        
        // Mark step as executed
        setExecutedSteps(prev => new Set([...prev, currentNode.id]));
        
        // Wait before next step
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Workflow completed
      setTimeout(() => {
        setIsExecuting(false);
        setCurrentExecutingStep(null);
        alert('Workflow completed successfully!');
      }, 500);
      
    } catch (error) {
      console.error('Workflow execution error:', error);
      alert(`Error running workflow: ${error.message}`);
      setIsExecuting(false);
      setCurrentExecutingStep(null);
      setExecutedSteps(new Set());
    }
  };

  // Calculate execution order for ReactFlow nodes
  const calculateExecutionOrder = (nodes, edges) => {
    const nodeMap = new Map(nodes.map(node => [node.id, node]));
    const incomingEdges = new Map();
    const outgoingEdges = new Map();
    
    // Initialize maps
    nodes.forEach(node => {
      incomingEdges.set(node.id, []);
      outgoingEdges.set(node.id, []);
    });
    
    // Populate edge maps
    edges.forEach(edge => {
      outgoingEdges.get(edge.source)?.push(edge.target);
      incomingEdges.get(edge.target)?.push(edge.source);
    });
    
    // Find trigger nodes (no incoming edges)
    const triggerNodes = nodes.filter(node => 
      node.data?.category === 'trigger' || incomingEdges.get(node.id)?.length === 0
    );
    
    if (triggerNodes.length === 0) {
      return nodes; // Fallback to all nodes if no triggers found
    }
    
    // Topological sort for execution order
    const executionOrder = [];
    const visited = new Set();
    const visiting = new Set();
    
    const visit = (nodeId) => {
      if (visiting.has(nodeId)) {
        throw new Error(`Circular dependency detected involving node ${nodeId}`);
      }
      if (visited.has(nodeId)) return;
      
      visiting.add(nodeId);
      
      const dependencies = incomingEdges.get(nodeId) || [];
      dependencies.forEach(depId => visit(depId));
      
      visiting.delete(nodeId);
      visited.add(nodeId);
      
      const node = nodeMap.get(nodeId);
      if (node) {
        executionOrder.push(node);
      }
    };
    
    // Start from trigger nodes
    triggerNodes.forEach(trigger => visit(trigger.id));
    
    // Add any remaining nodes
    nodes.forEach(node => {
      if (!visited.has(node.id)) {
        visit(node.id);
      }
    });
    
    return executionOrder;
  };

  // Execute individual node based on its type and configuration
  const executeNode = async (node, config) => {
    console.log(`Executing node: ${node.data.label}`, config);
    
    switch (node.data.type) {
      case 'schedule':
        await executeScheduleNode(node, config);
        break;
      case 'webhook':
        await executeWebhookNode(node, config);
        break;
      case 'email-received':
        await executeEmailTriggerNode(node, config);
        break;
      case 'instagram-post':
      case 'facebook-post':
      case 'twitter-post':
      case 'linkedin-post':
        await executeSocialMediaPost(node, config);
        break;
      case 'send-email':
        await executeSendEmail(node, config);
        break;
      case 'slack-message':
        await executeSlackMessage(node, config);
        break;
      case 'ai-content':
        await executeAIContent(node, config);
        break;
      case 'condition':
        await executeCondition(node, config);
        break;
      case 'http-request':
        await executeHTTPRequest(node, config);
        break;
      default:
        console.log(`No specific executor for node type: ${node.data.type}`);
        await new Promise(resolve => setTimeout(resolve, 500)); // Default delay
    }
  };

  // Individual node executors
  const executeScheduleNode = async (node, config) => {
    console.log('Schedule trigger activated', config);
    // Schedule triggers are typically just starting points
  };

  const executeWebhookNode = async (node, config) => {
    console.log('Webhook trigger activated', config);
    // Webhook triggers are typically just starting points
  };

  const executeEmailTriggerNode = async (node, config) => {
    console.log('Email trigger activated', config);
    // Email triggers are typically just starting points
  };

  const executeSocialMediaPost = async (node, config) => {
    console.log(`Creating ${node.data.type} post`, config);
    // Simulate API call to social media platform
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    if (!config.content) {
      throw new Error(`${node.data.label} requires content to be configured`);
    }
  };

  const executeSendEmail = async (node, config) => {
    console.log('Sending email', config);
    // Simulate email sending
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    if (!config.to || !config.subject) {
      throw new Error('Email node requires recipient and subject to be configured');
    }
  };

  const executeSlackMessage = async (node, config) => {
    console.log('Sending Slack message', config);
    // Simulate Slack API call
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const executeAIContent = async (node, config) => {
    console.log('Generating AI content', config);
    // Simulate AI content generation
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    if (!config.prompt) {
      throw new Error('AI Content node requires a prompt to be configured');
    }
  };

  const executeCondition = async (node, config) => {
    console.log('Evaluating condition', config);
    // Simulate condition evaluation
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (!config.checkValue || !config.compareValue) {
      throw new Error('Condition node requires values to be configured');
    }
  };

  const executeHTTPRequest = async (node, config) => {
    console.log('Making HTTP request', config);
    // Simulate HTTP request
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    if (!config.url) {
      throw new Error('HTTP Request node requires a URL to be configured');
    }
  };

  const pauseWorkflow = () => {
    if (isExecuting) {
      setIsExecuting(false);
      setCurrentExecutingStep(null);
      setExecutedSteps(new Set());
      alert('Workflow execution paused');
    }
  };

  // Open file in default system app
  const openInDefaultApp = (fileData, fileType) => {
    // Create blob from file data
    const blob = new Blob([fileData], { type: getMimeType(fileType) });
    const url = URL.createObjectURL(blob);

    // Try to open in new tab/window (browser will handle with default app)
    window.open(url, '_blank');

    // Clean up after a delay
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };

  // Get MIME type for file
  const getMimeType = (fileType) => {
    const types = {
      'image': 'image/png',
      'video': 'video/mp4',
      'file': 'application/octet-stream'
    };
    return types[fileType] || 'application/octet-stream';
  };

  // Calculate smart path between nodes to avoid overlaps
  const calculateSmartPath = (fromNode, toNode, allNodes) => {
    if (!fromNode || !toNode) return null;

    // Calculate optimal connection points based on relative positions
    let fromX, fromY, toX, toY;
    
    // Determine best connection sides based on relative positions
    const fromCenterX = fromNode.x + fromNode.width / 2;
    const fromCenterY = fromNode.y + fromNode.height / 2;
    const toCenterX = toNode.x + toNode.width / 2;
    const toCenterY = toNode.y + toNode.height / 2;
    
    // Connect from right side of source to left side of destination if source is to the left
    if (fromCenterX < toCenterX) {
      fromX = fromNode.x + fromNode.width;
      fromY = fromNode.y + fromNode.height / 2;
      toX = toNode.x - 10; // Offset for arrow
      toY = toNode.y + toNode.height / 2;
    }
    // Connect from left to right if source is to the right
    else if (fromCenterX > toCenterX) {
      fromX = fromNode.x;
      fromY = fromNode.y + fromNode.height / 2;
      toX = toNode.x + toNode.width + 10; // Offset for arrow
      toY = toNode.y + toNode.height / 2;
    }
    // Connect vertically if nodes are aligned horizontally
    else {
      fromX = fromNode.x + fromNode.width / 2;
      toX = toNode.x + toNode.width / 2;
      
      if (fromCenterY < toCenterY) {
        fromY = fromNode.y + fromNode.height;
        toY = toNode.y - 10; // Offset for arrow
      } else {
        fromY = fromNode.y;
        toY = toNode.y + toNode.height + 10; // Offset for arrow
      }
    }

    // Check if direct path would intersect with other nodes
    const hasObstacle = allNodes.some(node => {
      if (node.id === fromNode.id || node.id === toNode.id) return false;
      
      // Check if the line passes through this node
      return isLineIntersectingRect(
        fromX, fromY, toX, toY,
        node.x - 5, node.y - 5, node.x + node.width + 5, node.y + node.height + 5
      );
    });

    if (!hasObstacle) {
      // Smooth curved path if no obstacles
      const controlPointOffset = Math.abs(toX - fromX) * 0.5;
      const midX = (fromX + toX) / 2;
      
      if (Math.abs(fromX - toX) > Math.abs(fromY - toY)) {
        // Horizontal connection
        return `M ${fromX} ${fromY} C ${fromX + controlPointOffset} ${fromY}, ${toX - controlPointOffset} ${toY}, ${toX} ${toY}`;
      } else {
        // Vertical connection
        const controlPointOffsetY = Math.abs(toY - fromY) * 0.5;
        return `M ${fromX} ${fromY} C ${fromX} ${fromY + controlPointOffsetY}, ${toX} ${toY - controlPointOffsetY}, ${toX} ${toY}`;
      }
    } else {
      // Smart routing around obstacles with right angles
      const offsetX = 40;
      const offsetY = 40;
      
      if (Math.abs(fromX - toX) > Math.abs(fromY - toY)) {
        // Route horizontally then vertically
        const midX = fromX < toX ? Math.max(fromX + offsetX, (fromX + toX) / 2) : Math.min(fromX - offsetX, (fromX + toX) / 2);
        return `M ${fromX} ${fromY} L ${midX} ${fromY} L ${midX} ${toY} L ${toX} ${toY}`;
      } else {
        // Route vertically then horizontally
        const midY = fromY < toY ? Math.max(fromY + offsetY, (fromY + toY) / 2) : Math.min(fromY - offsetY, (fromY + toY) / 2);
        return `M ${fromX} ${fromY} L ${fromX} ${midY} L ${toX} ${midY} L ${toX} ${toY}`;
      }
    }
  };

  // Check if line intersects with rectangle
  const isLineIntersectingRect = (x1, y1, x2, y2, rectLeft, rectTop, rectRight, rectBottom) => {
    // Check if line segment intersects with rectangle
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    // Check if bounding boxes overlap
    if (maxX < rectLeft || minX > rectRight || maxY < rectTop || minY > rectBottom) {
      return false;
    }

    // More detailed intersection check
    const dx = x2 - x1;
    const dy = y2 - y1;
    
    // Check intersection with each edge of the rectangle
    const edges = [
      { x1: rectLeft, y1: rectTop, x2: rectRight, y2: rectTop },
      { x1: rectRight, y1: rectTop, x2: rectRight, y2: rectBottom },
      { x1: rectRight, y1: rectBottom, x2: rectLeft, y2: rectBottom },
      { x1: rectLeft, y1: rectBottom, x2: rectLeft, y2: rectTop }
    ];

    return edges.some(edge => {
      const edgeDx = edge.x2 - edge.x1;
      const edgeDy = edge.y2 - edge.y1;
      
      const denominator = dx * edgeDy - dy * edgeDx;
      if (Math.abs(denominator) < 0.0001) return false;
      
      const t = ((edge.x1 - x1) * edgeDy - (edge.y1 - y1) * edgeDx) / denominator;
      const u = ((edge.x1 - x1) * dy - (edge.y1 - y1) * dx) / denominator;
      
      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    });
  };

  // Handle connection dragging for manual routing
  const handleConnectionDragStart = (e, connectionIndex) => {
    e.stopPropagation();
    setSelectedConnection(connectionIndex);
    setDraggingConnection(true);
    
    const handleMouseMove = (e) => {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Find nearest node for snapping
      const nearestNode = canvasNodes.find(node => {
        const nodeX = node.x + node.width / 2;
        const nodeY = node.y + node.height / 2;
        const distance = Math.sqrt((x - nodeX) ** 2 + (y - nodeY) ** 2);
        return distance < 50;
      });
      
      if (nearestNode && selectedConnection !== null) {
        // Update connection to new node
        setConnections(prev => {
          const updated = [...prev];
          if (e.shiftKey) {
            // Shift+drag to change source
            updated[selectedConnection] = {
              ...updated[selectedConnection],
              from: nearestNode.id
            };
          } else {
            // Normal drag to change destination
            updated[selectedConnection] = {
              ...updated[selectedConnection],
              to: nearestNode.id
            };
          }
          return updated;
        });
      }
    };

    const handleMouseUp = () => {
      setDraggingConnection(false);
      setSelectedConnection(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };


  return (
    <>
      <div className="strategy-canvas-page">
        <div className="strategy-editor">
          {/* Main Content - matching ChatBot layout */}
          <div className="editor-content">
            {/* Collapsible Workflow History Sidebar */}
            <div className={`workflow-history-sidebar ${showWorkflowHistory ? 'open' : ''}`}>
              <div className="sidebar-header">
                <h3>
                  <FaHistory />
                  Workflow History
                </h3>
                <button 
                  className="sidebar-close-btn" 
                  onClick={() => setShowWorkflowHistory(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="chat-sessions">
                {chatSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${activeChatSession === session.id ? 'active' : ''}`}
                    onClick={() => {
                      setActiveChatSession(session.id);
                      setShowWorkflowHistory(false); // Close sidebar after selection
                    }}
                  >
                    <div className="session-content">
                      <div className="session-title">{session.title}</div>
                      <div className="session-timestamp">{session.timestamp}</div>
                    </div>
                    <div className="session-actions">
                      <button 
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteChatSession(session.id);
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
                
                {chatSessions.length === 0 && (
                  <div className="empty-state">
                    <p>No saved workflows</p>
                    <button 
                      className="create-first-workflow"
                      onClick={() => {
                        createNewChatSession();
                        setShowWorkflowHistory(false);
                      }}
                    >
                      Create First Workflow
                    </button>
                  </div>
                )}
              </div>
            </div>

          {/* Workflow Node Palette */}
          <div className="workflow-node-palette" id="node-palette">
            <div className="palette-header">
              <h3>Workflow Nodes</h3>
            </div>
            <div className="node-categories">
              {['trigger', 'social', 'action', 'ecommerce', 'ai', 'logic', 'function'].map(category => (
                <div key={category} className="node-category">
                  <h4 className={`category-title ${category}`}>
                    {category === 'trigger' && '🟢 Triggers'}
                    {category === 'social' && '📱 Social Media'}
                    {category === 'action' && '🔵 Actions'}
                    {category === 'ecommerce' && '🛒 E-commerce'}
                    {category === 'ai' && '🤖 AI Tools'}
                    {category === 'logic' && '🟡 Logic'}
                    {category === 'function' && '🟣 Functions'}
                  </h4>
                  <div className="category-nodes">
                    {workflowNodeTypes.filter(node => node.category === category).map(nodeType => {
                      const IconComponent = nodeType.icon;
                      const connectionStatus = getNodeConnectionStatus(nodeType.id);
                      const hasRequiredConnections = nodeType.requiredConnections && nodeType.requiredConnections.length > 0;
                      
                      return (
                        <div
                          key={nodeType.id}
                          className={`palette-node ${connectionStatus.status}`}
                          data-node-id={nodeType.id}
                          draggable
                          onDragStart={(e) => handleNodeDragStart(e, nodeType)}
                          onDragEnd={handleNodeDragEnd}
                          onClick={() => handlePaletteNodeClick(nodeType)}
                          style={{ borderColor: nodeType.color }}
                          title={hasRequiredConnections ? connectionStatus.message : `${nodeType.name} - Click to add to canvas or drag and drop`}
                        >
                          <div className="node-content">
                            <IconComponent style={{ color: nodeType.color }} />
                            <span>{nodeType.name}</span>
                          </div>
                          {hasRequiredConnections && (
                            <div className="connection-indicator">
                              {connectionStatus.status === 'connected' ? (
                                <FaCheckCircle className="connected" />
                              ) : (
                                <FaExclamationTriangle className="missing" />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Workflow Canvas Area */}
          <div className="workflow-main-area">
            {/* Workflow Toolbar */}
            <div className="workflow-toolbar" id="workflow-toolbar">
              <div className="toolbar-left">
                <button 
                  className={`toolbar-btn ${isExecuting ? 'executing' : 'primary'}`}
                  onClick={runWorkflow}
                  disabled={isExecuting}
                >
                  <FaPlay /> {isExecuting ? 'Running...' : 'Run'}
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={pauseWorkflow}
                  disabled={!isExecuting}
                >
                  <FaStop /> Pause
                </button>
              </div>
              <div className="toolbar-center">
                <div className="workflow-title-row">
                  {isEditingTitle ? (
                    <div className="title-edit-container">
                      <input
                        type="text"
                        value={tempTitle}
                        onChange={(e) => setTempTitle(e.target.value)}
                        onKeyPress={handleTitleKeyPress}
                        onBlur={handleTitleSave}
                        className="title-edit-input"
                        autoFocus
                        maxLength={50}
                      />
                    </div>
                  ) : (
                    <span 
                      className="workflow-title editable"
                      onClick={handleTitleClick}
                      title="Click to edit workflow name"
                    >
                      {chatSessions.find(s => s.id === activeChatSession)?.title || 'New Workflow'}
                    </span>
                  )}
                </div>
                
                {/* Workflow Node Selection Buttons */}
                {(() => {
                  const workflowNodes = getWorkflowNodesInOrder();
                  return workflowNodes.length > 0 && (
                    <div className="workflow-node-selector">
                      <span className="selector-label">Workflow Chain ({workflowNodes.length} nodes):</span>
                      <div className="node-number-buttons">
                        {workflowNodes.map((node, index) => (
                          <button
                            key={node.id}
                            className={`node-number-btn ${selectedNode?.id === node.id ? 'active' : ''}`}
                            onClick={() => selectWorkflowNode(index)}
                            title={`${index + 1}. ${node.data?.label || 'Node'} (${node.data?.category || 'unknown'})`}
                          >
                            {index + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Workflow Steps */}
                {workflowSteps.length > 0 && (
                  <div className="workflow-steps">
                    {workflowSteps.map((step, index) => (
                      <button
                        key={step.id}
                        className={`step-button ${highlightedStep === index ? 'active' : ''}`}
                        onClick={() => handleStepClick(index)}
                        title={step.name}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="toolbar-right">
                <button 
                  className="toolbar-btn"
                  onClick={() => setShowTemplates(!showTemplates)}
                >
                  <FaCopy /> Templates
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => {
                    const currentTitle = chatSessions.find(s => s.id === activeChatSession)?.title || 'Build a Workflow';
                    if (!workflowName.trim()) {
                      setWorkflowName(currentTitle);
                    }
                    setShowSaveWorkflow(true);
                  }}
                  disabled={canvasNodes.length === 0}
                >
                  <FaSave /> Save
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => setShowCreateTemplate(true)}
                  disabled={canvasNodes.length === 0}
                >
                  <FaCopy /> Save as Template
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => setShowExportOptions(true)}
                  disabled={canvasNodes.length === 0}
                >
                  Export
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => setShowWorkflowHistory(!showWorkflowHistory)}
                  title="Workflow History"
                >
                  <FaHistory />
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={createNewChatSession}
                  title="New Workflow"
                >
                  <FaPlus />
                </button>
              </div>
            </div>

            {/* Enhanced ReactFlow Canvas */}
            <FlowCanvas
              selectedNode={selectedNode}
              setSelectedNode={setSelectedNode}
              isExecuting={isExecuting}
              currentExecutingNode={currentExecutingStep}
              executedNodes={executedSteps}
              onNodesChange={(changes) => {
                // Handle ReactFlow node changes here if needed
                console.log('Nodes changed:', changes);
              }}
              onEdgesChange={(changes) => {
                // Handle ReactFlow edge changes here if needed
                console.log('Edges changed:', changes);
              }}
            />

            {/* Node Settings Panel */}
            <NodeSettingsPanel
              node={selectedNode}
              isVisible={!!selectedNode}
              serviceConnections={serviceConnections}
              onClose={() => setSelectedNode(null)}
              onSave={(nodeId, settings) => {
                // Save node configuration using the FlowCanvas API
                if (window.flowCanvasAPI && window.flowCanvasAPI.saveNodeConfiguration) {
                  window.flowCanvasAPI.saveNodeConfiguration(nodeId, settings);
                }
                console.log('Saved node settings:', nodeId, settings);
              }}
            />

              {/* Chat Input Area at Bottom */}
              <div className="chat-input-area">
                <div className="chat-input-container">
                  <div className="input-wrapper-modern">
                    <button 
                      className="attach-btn-modern" 
                      title="Attach file or media"
                      onClick={handleFileSelect}
                    >
                      <FaPlus />
                    </button>
                    <div className="input-group-modern">
                      <input
                        ref={inputRef}
                        type="text"
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={handleChatKeyPress}
                        placeholder="Describe your workflow: 'email automation', 'social media posting', 'order processing', etc..."
                        className="chat-input-modern"
                      />
                      <button 
                        className="launch-btn-modern"
                        onClick={sendChatMessage}
                        disabled={(!messageInput.trim() && selectedFiles.length === 0) || isTyping}
                      >
                        {isTyping ? 'Generating...' : 'Generate Workflow'}
                      </button>
                    </div>
                    
                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.mp4,.mov,.avi"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                    
                    {/* Selected files display */}
                    {selectedFiles.length > 0 && (
                      <div className="selected-files">
                        {selectedFiles.map((file, index) => {
                          const IconComponent = getFileIcon(file);
                          const isMedia = file.type.startsWith('image/') || file.type.startsWith('video/');
                          const fileUrl = URL.createObjectURL(file);
                          
                          return (
                            <div key={index} className="file-item">
                              {isMedia ? (
                                <div className="file-thumbnail">
                                  {file.type.startsWith('image/') ? (
                                    <img 
                                      src={fileUrl} 
                                      alt={file.name}
                                      className="thumbnail-image"
                                    />
                                  ) : (
                                    <video 
                                      src={fileUrl} 
                                      className="thumbnail-video"
                                      muted
                                    />
                                  )}
                                </div>
                              ) : (
                                <IconComponent className="file-icon" />
                              )}
                              <span className="file-name">{file.name}</span>
                              <span className="file-size">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                              <button 
                                className="remove-file-btn"
                                onClick={() => removeFile(index)}
                                title="Remove file"
                              >
                                <FaTimes />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tutorial Popup */}
        {showTutorial && (
          <div className="tutorial-overlay">
            <div className="tutorial-popup">
              <div className="tutorial-header">
                <h3>{tutorialSteps[tutorialStep].title}</h3>
                <button className="tutorial-close" onClick={skipTutorial}>
                  <FaTimes />
                </button>
              </div>
              <div className="tutorial-content">
                <p>{tutorialSteps[tutorialStep].content}</p>
              </div>
              <div className="tutorial-footer">
                <div className="tutorial-progress">
                  {tutorialSteps.map((_, index) => (
                    <div 
                      key={index}
                      className={`progress-dot ${index <= tutorialStep ? 'active' : ''}`}
                    />
                  ))}
                </div>
                <div className="tutorial-actions">
                  <button className="tutorial-btn secondary" onClick={skipTutorial}>
                    Skip
                  </button>
                  <button className="tutorial-btn primary" onClick={nextTutorialStep}>
                    {tutorialStep === tutorialSteps.length - 1 ? 'Get Started' : 'Next'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Modal */}
        {showTemplates && (
          <div className="templates-overlay">
            <div className="templates-modal">
              <div className="templates-header">
                <h2>Workflow Templates</h2>
                <div className="templates-actions">
                  <button 
                    className="create-template-btn"
                    onClick={() => setShowCreateTemplate(true)}
                    disabled={canvasNodes.length === 0}
                    title={canvasNodes.length === 0 ? "Create a workflow first" : "Save current workflow as template"}
                  >
                    <FaPlus /> Create Template
                  </button>
                  <button onClick={() => setShowTemplates(false)}>
                    <FaTimes />
                  </button>
                </div>
              </div>
              
              <div className="templates-content">
                {/* Custom Templates Section */}
                {customTemplates.length > 0 && (
                  <div className="templates-section">
                    <div className="section-header">
                      <FaFolderOpen className="section-icon" />
                      <h3>My Templates</h3>
                    </div>
                    <div className="templates-grid">
                      {customTemplates.map(template => (
                        <div key={template.id} className="template-card custom">
                          <div className="template-header">
                            <h4>{template.name}</h4>
                            <button 
                              className="delete-template-btn"
                              onClick={() => deleteCustomTemplate(template.id)}
                              title="Delete template"
                            >
                              <FaTrash />
                            </button>
                          </div>
                          <p>{template.description}</p>
                          <div className="template-preview">
                            <span>{template.nodes.length} nodes</span>
                            <span className="created-date">
                              {new Date(template.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <button 
                            className="template-use-btn"
                            onClick={() => loadCustomTemplate(template)}
                          >
                            Use Template
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Built-in Templates Section */}
                <div className="templates-section">
                  <div className="section-header">
                    <FaFolder className="section-icon" />
                    <h3>Built-in Templates</h3>
                  </div>
                  <div className="templates-grid">
                    {workflowTemplates.map(template => (
                      <div key={template.id} className="template-card">
                        <h4>{template.name}</h4>
                        <p>{template.description}</p>
                        <div className="template-preview">
                          <span>{template.nodes.length} nodes</span>
                        </div>
                        <button 
                          className="template-use-btn"
                          onClick={() => loadTemplate(template)}
                        >
                          Use Template
                        </button>
                      </div>
                    ))}
                    
                    {/* E-commerce Templates */}
                    <div className="template-card">
                      <h4>E-commerce Order Processor</h4>
                      <p>Automatically process orders from Shopify, update inventory, and send confirmation emails</p>
                      <div className="template-preview">
                        <span>6 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'ecommerce-processor',
                          nodes: [
                            { type: 'trigger-shopify-order', x: 100, y: 200 },
                            { type: 'action-shopify-inventory', x: 300, y: 200 },
                            { type: 'action-email', x: 500, y: 150 },
                            { type: 'action-slack', x: 500, y: 250 },
                            { type: 'action-google-sheets', x: 700, y: 200 },
                            { type: 'condition-if', x: 300, y: 100 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>

                    <div className="template-card">
                      <h4>Restaurant POS Integration</h4>
                      <p>Connect Toast POS with inventory management and customer notifications</p>
                      <div className="template-preview">
                        <span>5 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'restaurant-pos',
                          nodes: [
                            { type: 'trigger-toast-order', x: 100, y: 200 },
                            { type: 'action-toast-menu', x: 300, y: 200 },
                            { type: 'action-email', x: 500, y: 200 },
                            { type: 'action-instagram-post', x: 700, y: 150 },
                            { type: 'action-database', x: 700, y: 250 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>

                    <div className="template-card">
                      <h4>Payment & Square Integration</h4>
                      <p>Process Square payments and update customer records</p>
                      <div className="template-preview">
                        <span>4 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'square-payments',
                          nodes: [
                            { type: 'trigger-square-payment', x: 100, y: 200 },
                            { type: 'action-database', x: 300, y: 200 },
                            { type: 'action-email', x: 500, y: 200 },
                            { type: 'action-google-sheets', x: 700, y: 200 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                    
                    {/* AI-Powered Templates */}
                    <div className="template-card">
                      <h4>AI Content Generator</h4>
                      <p>Generate social media content using OpenAI and post across platforms</p>
                      <div className="template-preview">
                        <span>5 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'ai-content-generator',
                          nodes: [
                            { type: 'trigger-schedule', x: 100, y: 200 },
                            { type: 'openai-chat-completion', x: 300, y: 200 },
                            { type: 'openai-image-generation', x: 500, y: 200 },
                            { type: 'action-instagram-post', x: 700, y: 150 },
                            { type: 'action-twitter-tweet', x: 700, y: 250 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                    
                    <div className="template-card">
                      <h4>Lead Capture & Nurturing</h4>
                      <p>Capture leads from forms, save to Airtable, and send welcome sequence</p>
                      <div className="template-preview">
                        <span>6 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'lead-nurturing',
                          nodes: [
                            { type: 'trigger-form-typeform', x: 100, y: 200 },
                            { type: 'airtable-create-record', x: 300, y: 200 },
                            { type: 'action-gmail-send', x: 500, y: 150 },
                            { type: 'function-delay', x: 500, y: 250 },
                            { type: 'action-slack-message', x: 700, y: 200 },
                            { type: 'segment-track-event', x: 900, y: 200 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                    
                    <div className="template-card">
                      <h4>Customer Support Automation</h4>
                      <p>Route support tickets to Slack and create follow-up tasks in Notion</p>
                      <div className="template-preview">
                        <span>5 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'support-automation',
                          nodes: [
                            { type: 'trigger-email-gmail', x: 100, y: 200 },
                            { type: 'condition-if', x: 300, y: 200 },
                            { type: 'action-slack-message', x: 500, y: 150 },
                            { type: 'notion-create-page', x: 500, y: 250 },
                            { type: 'action-gmail-send', x: 700, y: 200 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                    
                    <div className="template-card">
                      <h4>E-commerce Analytics Dashboard</h4>
                      <p>Pull data from multiple platforms and consolidate in Google Sheets</p>
                      <div className="template-preview">
                        <span>7 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'analytics-dashboard',
                          nodes: [
                            { type: 'trigger-schedule', x: 100, y: 200 },
                            { type: 'trigger-shopify-order', x: 100, y: 100 },
                            { type: 'action-google-analytics-report', x: 300, y: 200 },
                            { type: 'mixpanel-track-event', x: 300, y: 300 },
                            { type: 'transform-json', x: 500, y: 200 },
                            { type: 'action-google-sheets-add', x: 700, y: 200 },
                            { type: 'action-slack-message', x: 900, y: 200 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                    
                    <div className="template-card">
                      <h4>Multi-Platform Publishing</h4>
                      <p>Create content once and distribute across all social media channels</p>
                      <div className="template-preview">
                        <span>8 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'multi-platform-publishing',
                          nodes: [
                            { type: 'trigger-webhook', x: 100, y: 200 },
                            { type: 'transform-text', x: 300, y: 200 },
                            { type: 'condition-switch', x: 500, y: 200 },
                            { type: 'action-instagram-post', x: 700, y: 100 },
                            { type: 'action-facebook-post', x: 700, y: 150 },
                            { type: 'action-twitter-tweet', x: 700, y: 200 },
                            { type: 'action-linkedin-post', x: 700, y: 250 },
                            { type: 'airtable-create-record', x: 900, y: 200 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                    
                    <div className="template-card">
                      <h4>RSS to Social Media</h4>
                      <p>Monitor RSS feeds and automatically share new content on social platforms</p>
                      <div className="template-preview">
                        <span>6 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'rss-to-social',
                          nodes: [
                            { type: 'trigger-rss-feed', x: 100, y: 200 },
                            { type: 'condition-filter', x: 300, y: 200 },
                            { type: 'transform-text', x: 500, y: 200 },
                            { type: 'action-twitter-tweet', x: 700, y: 150 },
                            { type: 'action-linkedin-post', x: 700, y: 250 },
                            { type: 'action-slack-message', x: 900, y: 200 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                    
                    <div className="template-card">
                      <h4>Event Registration Flow</h4>
                      <p>Handle event registrations with payment processing and calendar integration</p>
                      <div className="template-preview">
                        <span>7 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'event-registration',
                          nodes: [
                            { type: 'trigger-form-typeform', x: 100, y: 200 },
                            { type: 'stripe-create-payment', x: 300, y: 200 },
                            { type: 'condition-if', x: 500, y: 200 },
                            { type: 'google-calendar-event', x: 700, y: 150 },
                            { type: 'action-gmail-send', x: 700, y: 250 },
                            { type: 'airtable-create-record', x: 900, y: 200 },
                            { type: 'action-slack-message', x: 1100, y: 200 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                    
                    <div className="template-card">
                      <h4>Discord Community Bot</h4>
                      <p>Automate Discord community management with welcome messages and role assignment</p>
                      <div className="template-preview">
                        <span>5 nodes</span>
                      </div>
                      <button 
                        className="template-use-btn"
                        onClick={() => loadTemplate({
                          id: 'discord-community-bot',
                          nodes: [
                            { type: 'trigger-webhook', x: 100, y: 200 },
                            { type: 'condition-if', x: 300, y: 200 },
                            { type: 'discord-send-message', x: 500, y: 150 },
                            { type: 'airtable-create-record', x: 500, y: 250 },
                            { type: 'function-delay', x: 700, y: 200 }
                          ]
                        })}
                      >
                        Use Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Template Modal */}
        {showCreateTemplate && (
          <div className="templates-overlay">
            <div className="create-template-modal">
              <div className="modal-header">
                <h3>Save Workflow Template</h3>
                <button onClick={() => setShowCreateTemplate(false)}>
                  <FaTimes />
                </button>
              </div>
              <div className="modal-content">
                <div className="form-group">
                  <label>Template Name</label>
                  <input 
                    type="text" 
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name..."
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea 
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder="Describe what this template does..."
                    rows="3"
                    maxLength={200}
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select 
                    value={workflowCategory}
                    onChange={(e) => setWorkflowCategory(e.target.value)}
                  >
                    <option value="general">General</option>
                    <option value="marketing">Marketing</option>
                    <option value="sales">Sales</option>
                    <option value="e-commerce">E-commerce</option>
                    <option value="analytics">Analytics</option>
                    <option value="customer-service">Customer Service</option>
                    <option value="social-media">Social Media</option>
                    <option value="email-automation">Email Automation</option>
                    <option value="data-processing">Data Processing</option>
                    <option value="content-creation">Content Creation</option>
                  </select>
                </div>
                <div className="template-preview-info">
                  <p>This template will include {canvasNodes.length} nodes and {connections.length} connections from your current workflow.</p>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn secondary"
                  onClick={() => setShowCreateTemplate(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn primary"
                  onClick={saveCustomTemplate}
                  disabled={!templateName.trim()}
                >
                  <FaSave /> Save Template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Workflow Modal */}
        {showSaveWorkflow && (
          <div className="templates-overlay">
            <div className="save-workflow-modal">
              <div className="modal-header">
                <h3>Save Workflow</h3>
                <button onClick={() => setShowSaveWorkflow(false)}>
                  <FaTimes />
                </button>
              </div>
              <div className="modal-content">
                <div className="form-group">
                  <label>Workflow Name</label>
                  <input 
                    type="text" 
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    placeholder="Enter workflow name..."
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select 
                    value={workflowCategory}
                    onChange={(e) => setWorkflowCategory(e.target.value)}
                  >
                    <option value="personal">Personal</option>
                    <option value="general">General</option>
                    <option value="marketing">Marketing</option>
                    <option value="sales">Sales</option>
                    <option value="ecommerce">E-commerce</option>
                    <option value="analytics">Analytics</option>
                    <option value="customer-service">Customer Service</option>
                  </select>
                </div>
                
                {/* Workflow Output Section */}
                {workflowOutput && (
                  <div className="workflow-output-section">
                    <h4>Workflow Output</h4>
                    <div className="output-preview">
                      <div className="output-type-info">
                        <span className={`output-type-badge ${workflowOutput.type}`}>
                          {workflowOutput.type === 'image' && <FaImage />}
                          {workflowOutput.type === 'video' && <FaVideo />}
                          {workflowOutput.type === 'file' && <FaFile />}
                          {workflowOutput.type.charAt(0).toUpperCase() + workflowOutput.type.slice(1)}
                        </span>
                        {workflowOutput.name && (
                          <span className="output-name">{workflowOutput.name}</span>
                        )}
                      </div>
                      
                      <div className="output-save-options">
                        <label>Save Output Options:</label>
                        <div className="save-options-grid">
                          <button 
                            className="save-option-btn"
                            onClick={() => downloadWorkflowOutput('original')}
                          >
                            <FaDownload /> Download Original
                          </button>
                          
                          {workflowOutput.type === 'image' && (
                            <>
                              <button 
                                className="save-option-btn"
                                onClick={() => downloadWorkflowOutput('png')}
                              >
                                <FaImage /> Save as PNG
                              </button>
                              <button 
                                className="save-option-btn"
                                onClick={() => downloadWorkflowOutput('jpg')}
                              >
                                <FaImage /> Save as JPG
                              </button>
                            </>
                          )}
                          
                          {workflowOutput.type === 'video' && (
                            <>
                              <button 
                                className="save-option-btn"
                                onClick={() => downloadWorkflowOutput('mp4')}
                              >
                                <FaVideo /> Save as MP4
                              </button>
                              <button 
                                className="save-option-btn"
                                onClick={() => downloadWorkflowOutput('gif')}
                              >
                                <FaImage /> Convert to GIF
                              </button>
                            </>
                          )}
                          
                          <button 
                            className="save-option-btn"
                            onClick={() => saveToCloud()}
                          >
                            <FaCloudUploadAlt /> Save to Cloud
                          </button>
                          
                          <button 
                            className="save-option-btn"
                            onClick={() => shareOutput()}
                          >
                            <FaLink /> Share Output
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="workflow-preview-info">
                  <p>This workflow contains {canvasNodes.length} nodes and {connections.length} connections.</p>
                  {workflowOutput && (
                    <p>Output will be saved with the workflow for future reference.</p>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn secondary"
                  onClick={() => setShowSaveWorkflow(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn primary"
                  onClick={saveWorkflow}
                  disabled={!workflowName.trim()}
                >
                  <FaSave /> Save Workflow
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Workflow Confirmation Modal */}
        {showNewWorkflowConfirm && (
          <div className="templates-overlay">
            <div className="create-template-modal">
              <div className="modal-header">
                <h3>Start New Workflow</h3>
                <button onClick={() => setShowNewWorkflowConfirm(false)}>
                  <FaTimes />
                </button>
              </div>
              <div className="modal-content">
                <div className="form-group">
                  <p>You have an existing workflow with {canvasNodes.length} nodes. Would you like to save it as a template before starting a new workflow?</p>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn secondary"
                  onClick={() => {
                    createNewWorkflow();
                  }}
                >
                  No, Discard Current
                </button>
                <button 
                  className="btn primary"
                  onClick={saveCurrentAsTemplate}
                >
                  <FaSave /> Yes, Save as Template
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </>
  );
}

export default StrategyCanvas;